import { useState, useEffect, useCallback, useRef } from "react";
import { useAuth, API } from "../App";
import { useSearchParams } from "react-router-dom";
import axios from "axios";
import Sidebar from "../components/Sidebar";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Badge } from "../components/ui/badge";
import { Textarea } from "../components/ui/textarea";
import { toast } from "sonner";

const resolveFileUrl = (url) => {
  if (!url) return "";
  // Reescribir SIEMPRE al backend actual: URLs absolutas guardadas con un
  // dominio anterior rompen el visor por CORS aunque la descarga funcione.
  const apiIdx = url.indexOf("/api/");
  if (apiIdx !== -1) return `${API}${url.slice(apiIdx + 4)}`;
  if (url.startsWith("http")) return url;
  return url;
};
import {
  Newspaper, Image, FolderOpen, BookOpen, Building2, Plus, Trash2, Pin, PinOff,
  Upload, Download, Eye, X, ChevronLeft, ChevronRight, Loader2, FileText, Users, Edit, Search, LayoutGrid,
  Phone, Mail, Images, Link2, Check, Pencil, UploadCloud, Folder
} from "lucide-react";
import TabLayoutEditor from "../components/TabLayoutEditor";
import PdfViewer from "../components/PdfViewer";

const EFECTOS = [
  { value: "fade", label: "Desvanecer (Fade)" },
  { value: "slide", label: "Deslizar (Slide)" },
  { value: "zoom_in", label: "Acercar (Zoom In)" },
  { value: "zoom_out", label: "Alejar (Zoom Out)" },
  { value: "flip", label: "Giro 3D (Flip)" },
  { value: "blur", label: "Desenfoque (Blur)" },
  { value: "kenburns", label: "Kenburns (Cinematográfico)" }
];

function CarouselCard({ carousel, canPublish, onDelete, onImageClick, titleStyle, onChangeEfecto }) {
  const [idx, setIdx] = useState(0);
  const [prevIdx, setPrevIdx] = useState(0);
  const [animating, setAnimating] = useState(false);
  const [direction, setDirection] = useState(1); // 1=next, -1=prev
  const images = carousel.images || [];
  const total = images.length;
  const efecto = carousel.efecto_transicion || "fade";

  const goTo = useCallback((newIdx, dir = 1) => {
    if (animating || newIdx === idx) return;
    setPrevIdx(idx);
    setDirection(dir);
    setAnimating(true);
    setIdx(newIdx);
    setTimeout(() => setAnimating(false), 700);
  }, [idx, animating]);

  const prev = () => goTo((idx - 1 + total) % total, -1);
  const next = () => goTo((idx + 1) % total, 1);

  useEffect(() => {
    if (total <= 1) return;
    const timer = setInterval(() => goTo((idx + 1) % total, 1), 5000);
    return () => clearInterval(timer);
  }, [total, idx, goTo]);

  if (total === 0) return null;

  // CSS styles per effect
  const getImageStyle = (isActive) => {
    const base = { position: "absolute", inset: 0, width: "100%", height: "100%", objectFit: "contain", cursor: "pointer" };
    if (!animating) return { ...base, opacity: isActive ? 1 : 0 };

    switch (efecto) {
      case "fade":
        return { ...base, opacity: isActive ? 1 : 0, transition: "opacity 0.7s ease-in-out" };
      case "slide":
        return {
          ...base,
          opacity: 1,
          transform: isActive ? "translateX(0)" : `translateX(${isActive ? 0 : direction * -100}%)`,
          transition: "transform 0.6s ease-in-out, opacity 0.6s ease-in-out",
          ...(isActive ? {} : { opacity: 0 })
        };
      case "zoom_in":
        return { ...base, opacity: isActive ? 1 : 0, transform: isActive ? "scale(1)" : "scale(0.7)", transition: "opacity 0.7s ease, transform 0.7s ease" };
      case "zoom_out":
        return { ...base, opacity: isActive ? 1 : 0, transform: isActive ? "scale(1)" : "scale(1.3)", transition: "opacity 0.7s ease, transform 0.7s ease" };
      case "flip":
        return { ...base, opacity: isActive ? 1 : 0, transform: isActive ? "perspective(800px) rotateY(0deg)" : "perspective(800px) rotateY(90deg)", transition: "opacity 0.5s ease, transform 0.6s ease", backfaceVisibility: "hidden" };
      case "blur":
        return { ...base, opacity: isActive ? 1 : 0, filter: isActive ? "blur(0px)" : "blur(12px)", transition: "opacity 0.7s ease, filter 0.7s ease" };
      case "kenburns":
        return { ...base, opacity: isActive ? 1 : 0, transform: isActive ? "scale(1.05)" : "scale(1)", transition: "opacity 1s ease, transform 8s ease", objectFit: "cover" };
      default:
        return { ...base, opacity: isActive ? 1 : 0, transition: "opacity 0.7s ease" };
    }
  };

  return (
    <Card className="overflow-hidden" data-testid={`carousel-${carousel.id}`}>
      <CardContent className="p-4 pb-2 flex items-center justify-between">
        <h3 className="font-semibold text-lg text-slate-900 dark:text-white" style={titleStyle || {}}>{carousel.titulo}</h3>
        {canPublish && (
          <div className="flex items-center gap-2">
            <select className="text-xs border rounded px-2 py-1 dark:bg-slate-700 dark:border-slate-600" value={efecto}
              onChange={e => onChangeEfecto?.(carousel.id, e.target.value)}
              data-testid={`efecto-select-${carousel.id}`}>
              {EFECTOS.map(e => <option key={e.value} value={e.value}>{e.label}</option>)}
            </select>
            <Button variant="destructive" size="sm" onClick={onDelete} data-testid={`delete-carousel-${carousel.id}`}>
              <Trash2 className="w-3.5 h-3.5 mr-1" /> Eliminar
            </Button>
          </div>
        )}
      </CardContent>
      <div className="relative w-full h-[500px] bg-black overflow-hidden">
        {images.map((img, i) => (
          <img key={img.id || i} src={resolveFileUrl(img.url)} alt={img.nombre || ""}
            style={getImageStyle(i === idx)}
            onClick={() => i === idx && onImageClick?.({ url: img.url, nombre: img.nombre })}
          />
        ))}
        {total > 1 && (
          <>
            <button onClick={prev} data-testid="carousel-prev" className="absolute left-3 top-1/2 -translate-y-1/2 bg-black/50 hover:bg-black/70 text-white rounded-full w-10 h-10 flex items-center justify-center transition-colors z-10">
              <ChevronLeft className="w-5 h-5" />
            </button>
            <button onClick={next} data-testid="carousel-next" className="absolute right-3 top-1/2 -translate-y-1/2 bg-black/50 hover:bg-black/70 text-white rounded-full w-10 h-10 flex items-center justify-center transition-colors z-10">
              <ChevronRight className="w-5 h-5" />
            </button>
            <div className="absolute bottom-3 left-1/2 -translate-x-1/2 flex gap-2 z-10">
              {images.map((_, i) => (
                <button key={i} onClick={() => goTo(i, i > idx ? 1 : -1)} className={`w-2.5 h-2.5 rounded-full transition-all ${i === idx ? "bg-white scale-125" : "bg-white/50"}`} />
              ))}
            </div>
          </>
        )}
        <div className="absolute top-3 right-3 bg-black/60 text-white text-xs px-2 py-1 rounded-full z-10">
          {idx + 1} / {total}
        </div>
      </div>
      {carousel.descripcion && (
        <CardContent className="p-4 pt-2">
          <p className="text-sm text-slate-500">{carousel.descripcion}</p>
        </CardContent>
      )}
    </Card>
  );
}

export default function InformacionesGeneralesPage() {
  const { user, getAuthHeaders } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const [activeTab, setActiveTab] = useState(searchParams.get("tab") || "noticias");
  const [loading, setLoading] = useState(false);
  const highlightedId = searchParams.get("id") || null;
  const deepLinkProcessed = useRef(false);

  const BASE_URL = "https://softwareia.qasesores.com.do";

  // Permission: admin and gestion_humana can copy links
  const canCopyLink = user?.role?.toLowerCase() === "admin" || user?.role?.toLowerCase() === "administrador" || user?.role?.toLowerCase() === "gestion_humana";

  // Permission check
  const canPublish = user?.role?.toLowerCase() === "admin" || user?.role?.toLowerCase() === "administrador" || user?.puede_publicar;
  const isAdmin = ["admin", "administrador"].includes(user?.role?.toLowerCase());

  // ============ NOTICIAS STATE ============
  const [publicaciones, setPublicaciones] = useState([]);
  const [showPubDialog, setShowPubDialog] = useState(false);
  const [editingPub, setEditingPub] = useState(null);
  const [pubForm, setPubForm] = useState({ titulo: "", contenido: "", imagen_url: "", fijada: false, categoria: "general" });
  const [pubSearch, setPubSearch] = useState("");
  const [uploadingImg, setUploadingImg] = useState(false);

  // ============ GALERÍA STATE ============
  const [albumes, setAlbumes] = useState([]);
  const [selectedAlbum, setSelectedAlbum] = useState(null);
  const [fotos, setFotos] = useState([]);
  const [showAlbumDialog, setShowAlbumDialog] = useState(false);
  const [albumForm, setAlbumForm] = useState({ nombre: "", descripcion: "" });
  const [uploadingFoto, setUploadingFoto] = useState(false);
  const [lightboxFoto, setLightboxFoto] = useState(null);
  const [showGaleriaHtmlDialog, setShowGaleriaHtmlDialog] = useState(false);
  const [galeriaHtmlForm, setGaleriaHtmlForm] = useState({ titulo: "", descripcion: "", album_id: "", contenido_html: "" });
  const [editingGaleriaHtml, setEditingGaleriaHtml] = useState(null);
  const [galeriaHtmlPreview, setGaleriaHtmlPreview] = useState(false);
  const [viewingGaleriaHtml, setViewingGaleriaHtml] = useState(null);
  const [carousels, setCarousels] = useState([]);
  const [showCarouselDialog, setShowCarouselDialog] = useState(false);
  const [carouselForm, setCarouselForm] = useState({ titulo: "", descripcion: "", efecto_transicion: "fade" });
  const [carouselFiles, setCarouselFiles] = useState([]);
  const [uploadingCarousel, setUploadingCarousel] = useState(false);

  // ============ DOCUMENTOS STATE ============
  const [categorias, setCategorias] = useState([]);
  const [documentos, setDocumentos] = useState([]);
  const [selectedCat, setSelectedCat] = useState(null);
  const [showCatDialog, setShowCatDialog] = useState(false);
  const [catForm, setCatForm] = useState({ nombre: "", descripcion: "" });
  const [editingCat, setEditingCat] = useState(null);
  const [catParentId, setCatParentId] = useState(null);
  const [docFiles, setDocFiles] = useState([]);
  const [uploadDocProgress, setUploadDocProgress] = useState("");
  const [docSearch, setDocSearch] = useState("");
  const [showDocDialog, setShowDocDialog] = useState(false);
  const [docForm, setDocForm] = useState({ titulo: "", descripcion: "", categoria_id: "" });
  const [docFile, setDocFile] = useState(null);
  const [uploadingDoc, setUploadingDoc] = useState(false);
  const [showHtmlDocDialog, setShowHtmlDocDialog] = useState(false);
  const [htmlDocForm, setHtmlDocForm] = useState({ titulo: "", descripcion: "", categoria_id: "", contenido_html: "" });
  const [editingHtmlDoc, setEditingHtmlDoc] = useState(null);
  const [htmlPreviewMode, setHtmlPreviewMode] = useState(false);
  const [viewingHtmlDoc, setViewingHtmlDoc] = useState(null);
  const [viewingPdfDoc, setViewingPdfDoc] = useState(null);
  const [pdfLoadError, setPdfLoadError] = useState(false);

  const [pdfBlobUrl, setPdfBlobUrl] = useState(null);
  
  const openPdfViewer = async (doc) => {
    setPdfLoadError(false);
    setPdfBlobUrl(null);
    setViewingPdfDoc(doc);
    // Fetch PDF as blob for react-pdf
    try {
      const res = await fetch(resolveFileUrl(doc.url));
      if (!res.ok) { setPdfLoadError(true); return; }
      const blob = await res.blob();
      const blobUrl = URL.createObjectURL(blob);
      setPdfBlobUrl(blobUrl);
    } catch { setPdfLoadError(true); }
  };
  
  const closePdfViewer = () => {
    if (pdfBlobUrl) URL.revokeObjectURL(pdfBlobUrl);
    setPdfBlobUrl(null);
    setViewingPdfDoc(null);
    setPdfLoadError(false);
  };

  // ============ LECTURA STATE ============
  const [libros, setLibros] = useState([]);
  const [showLibroDialog, setShowLibroDialog] = useState(false);
  const [libroForm, setLibroForm] = useState({ titulo: "", autor: "", descripcion: "", enlace_externo: "", contenido_html: "" });
  const [libroPortadaFile, setLibroPortadaFile] = useState(null);
  const [editingLibro, setEditingLibro] = useState(null);
  const [libroHtmlPreview, setLibroHtmlPreview] = useState(false);
  const [viewingLibro, setViewingLibro] = useState(null);
  const [uploadingPdf, setUploadingPdf] = useState(false);

  // ============ DIRECTORIO STATE ============
  const [directorio, setDirectorio] = useState([]);
  const [showDirDialog, setShowDirDialog] = useState(false);
  const [editingDir, setEditingDir] = useState(null);
  const [dirForm, setDirForm] = useState({ nombre: "", extension: "", numero_directo: "", telefono: "", correo: "", departamento: "", cargo: "" });
  const [dirSearch, setDirSearch] = useState("");
  const [dirFilterDept, setDirFilterDept] = useState("");

  // ============ LAYOUT EDITOR STATE ============
  const [layoutMode, setLayoutMode] = useState(null);
  const [tabLayouts, setTabLayouts] = useState({});

  // Helper to get title style for a tab
  const getTitleStyle = (tabId) => {
    const ts = tabLayouts[tabId]?.title_style;
    if (!ts) return {};
    const style = {};
    if (ts.font_family) style.fontFamily = ts.font_family;
    if (ts.color) style.color = ts.color;
    if (ts.font_size) style.fontSize = ts.font_size;
    return style;
  };

  // Load Google Fonts dynamically based on title_style configs
  useEffect(() => {
    const fonts = new Set();
    Object.values(tabLayouts).forEach(layout => {
      const ff = layout?.title_style?.font_family;
      if (ff) {
        const match = ff.match(/'([^']+)'/);
        if (match) fonts.add(match[1]);
      }
    });
    if (fonts.size > 0) {
      const link = document.createElement("link");
      link.rel = "stylesheet";
      link.href = `https://fonts.googleapis.com/css2?${[...fonts].map(f => `family=${f.replace(/ /g, "+")}`).join("&")}&display=swap`;
      document.head.appendChild(link);
      return () => { try { document.head.removeChild(link); } catch {} };
    }
  }, [tabLayouts]);

  const fetchTabLayout = useCallback(async (tabId) => {
    try {
      const res = await axios.get(`${API}/informaciones/layout/${tabId}`, getAuthHeaders());
      setTabLayouts(prev => ({ ...prev, [tabId]: res.data }));
    } catch (err) { console.error(err); }
  }, [getAuthHeaders]);

  // ============ FETCH FUNCTIONS ============
  const fetchPublicaciones = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/informaciones/publicaciones`, getAuthHeaders());
      setPublicaciones(res.data);
    } catch (err) { console.error(err); }
  }, [getAuthHeaders]);

  const fetchAlbumes = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/informaciones/albumes`, getAuthHeaders());
      setAlbumes(res.data);
    } catch (err) { console.error(err); }
  }, [getAuthHeaders]);

  const fetchFotos = useCallback(async (albumId) => {
    try {
      const res = await axios.get(`${API}/informaciones/albumes/${albumId}/fotos`, getAuthHeaders());
      setFotos(res.data);
    } catch (err) { console.error(err); }
  }, [getAuthHeaders]);

  const fetchCarousels = useCallback(async (albumId) => {
    try {
      const res = await axios.get(`${API}/informaciones/albumes/${albumId}/carousels`, getAuthHeaders());
      setCarousels(res.data);
    } catch (err) { console.error(err); }
  }, [getAuthHeaders]);

  const fetchCategorias = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/informaciones/doc-categorias`, getAuthHeaders());
      setCategorias(res.data);
    } catch (err) { console.error(err); }
  }, [getAuthHeaders]);

  const fetchDocumentos = useCallback(async (catId) => {
    try {
      setDocSearch("");
      const url = catId ? `${API}/informaciones/documentos?categoria_id=${catId}` : `${API}/informaciones/documentos`;
      const res = await axios.get(url, getAuthHeaders());
      setDocumentos(res.data);
    } catch (err) { console.error(err); }
  }, [getAuthHeaders]);

  const fetchLibros = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/informaciones/libros`, getAuthHeaders());
      setLibros(res.data);
    } catch (err) { console.error(err); }
  }, [getAuthHeaders]);

  const fetchDirectorio = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/informaciones/directorio`, getAuthHeaders());
      setDirectorio(res.data);
    } catch (err) { console.error(err); }
  }, [getAuthHeaders]);

  useEffect(() => {
    fetchTabLayout(activeTab);
    if (activeTab === "noticias") fetchPublicaciones();
    else if (activeTab === "galeria") fetchAlbumes();
    else if (activeTab === "documentos") { fetchCategorias(); fetchDocumentos(); }
    else if (activeTab === "lectura") fetchLibros();
    else if (activeTab === "directorio") fetchDirectorio();
  }, [activeTab, fetchPublicaciones, fetchAlbumes, fetchCategorias, fetchDocumentos, fetchLibros, fetchDirectorio, fetchTabLayout]);

  // Deep link: open the specific item in view mode after data loads
  useEffect(() => {
    if (!highlightedId || deepLinkProcessed.current) return;
    
    const tab = searchParams.get("tab");
    
    // For documents - open in view mode directly
    if (tab === "documentos" && documentos.length > 0) {
      const doc = documentos.find(d => d.id === highlightedId);
      if (doc) {
        deepLinkProcessed.current = true;
        const isPdf = doc.tipo_archivo === "PDF" || (doc.url && doc.url.toLowerCase().endsWith(".pdf"));
        if (doc.tipo === "html") {
          setViewingHtmlDoc(doc);
        } else if (isPdf && doc.url) {
          openPdfViewer(doc);
        } else if (doc.url) {
          // For other file types, open in new tab
          window.open(resolveFileUrl(doc.url), "_blank");
        }
        return;
      }
    }
    
    // For noticias - scroll and highlight (noticias are read inline)
    if (tab === "noticias" && publicaciones.length > 0) {
      const pub = publicaciones.find(p => p.id === highlightedId);
      if (pub) {
        deepLinkProcessed.current = true;
        const el = document.querySelector(`[data-item-id="${highlightedId}"]`);
        if (el) {
          setTimeout(() => {
            el.scrollIntoView({ behavior: "smooth", block: "center" });
            el.classList.add("ring-2", "ring-teal-500", "ring-offset-2");
            setTimeout(() => el.classList.remove("ring-2", "ring-teal-500", "ring-offset-2"), 4000);
          }, 300);
        }
        return;
      }
    }
    
    // For libros - open in view mode
    if (tab === "lectura" && libros.length > 0) {
      const libro = libros.find(l => l.id === highlightedId);
      if (libro) {
        deepLinkProcessed.current = true;
        if (libro.contenido_html) {
          setViewingLibro(libro);
        } else if (libro.pdf_url) {
          window.open(resolveFileUrl(libro.pdf_url), "_blank");
        } else if (libro.enlace_externo) {
          window.open(libro.enlace_externo, "_blank");
        }
        return;
      }
    }
    
    // For galeria - open album
    if (tab === "galeria" && albumes.length > 0) {
      const album = albumes.find(a => a.id === highlightedId);
      if (album) {
        deepLinkProcessed.current = true;
        setSelectedAlbum(album);
        fetchFotos(album.id);
        fetchCarousels(album.id);
        return;
      }
    }
    
    // Fallback: just scroll to highlight
    const el = document.querySelector(`[data-item-id="${highlightedId}"]`);
    if (el) {
      deepLinkProcessed.current = true;
      setTimeout(() => {
        el.scrollIntoView({ behavior: "smooth", block: "center" });
        el.classList.add("ring-2", "ring-teal-500", "ring-offset-2");
        setTimeout(() => el.classList.remove("ring-2", "ring-teal-500", "ring-offset-2"), 4000);
      }, 300);
    }
  }, [highlightedId, publicaciones, documentos, libros, albumes]);

  // Copy link helper
  const copyItemLink = async (tab, itemId) => {
    try {
      // Generate a public token for this content
      const res = await axios.post(`${API}/informaciones/public/generate-link`, {
        tab, item_id: itemId
      }, getAuthHeaders());
      const token = res.data.token;
      const link = `${BASE_URL}/public/info/${token}`;
      await navigator.clipboard.writeText(link);
      toast.success("Link público copiado al portapapeles");
    } catch (err) {
      // Fallback to the old link format
      const link = `${BASE_URL}/informaciones-generales?tab=${tab}&id=${itemId}`;
      try {
        await navigator.clipboard.writeText(link);
      } catch {
        const input = document.createElement("input");
        input.value = link;
        document.body.appendChild(input);
        input.select();
        document.execCommand("copy");
        document.body.removeChild(input);
      }
      toast.success("Link copiado al portapapeles");
    }
  };

  // CopyLinkButton component
  const CopyLinkButton = ({ tab, itemId }) => {
    const [copied, setCopied] = useState(false);
    const [generating, setGenerating] = useState(false);
    if (!canCopyLink) return null;
    return (
      <Button
        variant="ghost"
        size="sm"
        className="text-slate-400 hover:text-teal-600"
        title="Copiar link público"
        data-testid={`copy-link-${itemId}`}
        disabled={generating}
        onClick={async (e) => {
          e.stopPropagation();
          setGenerating(true);
          await copyItemLink(tab, itemId);
          setGenerating(false);
          setCopied(true);
          setTimeout(() => setCopied(false), 2000);
        }}
      >
        {generating ? <Loader2 className="w-4 h-4 animate-spin" /> : copied ? <Check className="w-4 h-4 text-teal-600" /> : <Link2 className="w-4 h-4" />}
      </Button>
    );
  };

  // ============ PUBLICACIONES HANDLERS ============
  const handleUploadImage = async (file) => {
    setUploadingImg(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const res = await axios.post(`${API}/informaciones/upload-imagen`, fd, {
        ...getAuthHeaders(), headers: { ...getAuthHeaders().headers, "Content-Type": "multipart/form-data" }
      });
      setPubForm(prev => ({ ...prev, imagen_url: res.data.url }));
      toast.success("Imagen subida");
    } catch (err) { toast.error("Error al subir imagen"); }
    finally { setUploadingImg(false); }
  };

  const handleSavePub = async () => {
    if (!pubForm.titulo || !pubForm.contenido) return toast.error("Complete título y contenido");
    setLoading(true);
    try {
      if (editingPub) {
        await axios.put(`${API}/informaciones/publicaciones/${editingPub.id}`, pubForm, getAuthHeaders());
        toast.success("Publicación actualizada");
      } else {
        await axios.post(`${API}/informaciones/publicaciones`, pubForm, getAuthHeaders());
        toast.success("Publicación creada");
      }
      setShowPubDialog(false);
      setEditingPub(null);
      setPubForm({ titulo: "", contenido: "", imagen_url: "", fijada: false, categoria: "general" });
      fetchPublicaciones();
    } catch (err) { toast.error(err.response?.data?.detail || "Error"); }
    finally { setLoading(false); }
  };

  const handleDeletePub = async (id) => {
    if (!window.confirm("¿Eliminar esta publicación?")) return;
    try {
      await axios.delete(`${API}/informaciones/publicaciones/${id}`, getAuthHeaders());
      toast.success("Eliminada");
      fetchPublicaciones();
    } catch (err) { toast.error("Error"); }
  };

  const handleTogglePin = async (id) => {
    try {
      const res = await axios.post(`${API}/informaciones/publicaciones/${id}/toggle-pin`, {}, getAuthHeaders());
      toast.success(res.data.fijada ? "Publicación fijada" : "Publicación desfijada");
      fetchPublicaciones();
    } catch (err) { toast.error("Error"); }
  };

  // ============ GALERÍA HANDLERS ============
  const handleCreateAlbum = async () => {
    if (!albumForm.nombre) return toast.error("Ingrese nombre del álbum");
    try {
      await axios.post(`${API}/informaciones/albumes`, albumForm, getAuthHeaders());
      toast.success("Álbum creado");
      setShowAlbumDialog(false);
      setAlbumForm({ nombre: "", descripcion: "" });
      fetchAlbumes();
    } catch (err) { toast.error("Error"); }
  };

  const handleDeleteAlbum = async (id) => {
    if (!window.confirm("¿Eliminar álbum y todas sus fotos?")) return;
    try {
      await axios.delete(`${API}/informaciones/albumes/${id}`, getAuthHeaders());
      toast.success("Álbum eliminado");
      setSelectedAlbum(null);
      fetchAlbumes();
    } catch (err) { toast.error("Error"); }
  };

  const handleUploadFoto = async (e) => {
    const files = e.target.files;
    if (!files?.length || !selectedAlbum) return;
    setUploadingFoto(true);
    try {
      for (const file of files) {
        const fd = new FormData();
        fd.append("file", file);
        await axios.post(`${API}/informaciones/albumes/${selectedAlbum.id}/fotos`, fd, {
          ...getAuthHeaders(), headers: { ...getAuthHeaders().headers, "Content-Type": "multipart/form-data" }
        });
      }
      toast.success(`${files.length} foto(s) subida(s)`);
      fetchFotos(selectedAlbum.id);
      fetchAlbumes();
    } catch (err) { toast.error("Error al subir fotos"); }
    finally { setUploadingFoto(false); e.target.value = ""; }
  };

  const handleDeleteFoto = async (id) => {
    try {
      await axios.delete(`${API}/informaciones/fotos/${id}`, getAuthHeaders());
      toast.success("Foto eliminada");
      fetchFotos(selectedAlbum.id);
      fetchAlbumes();
    } catch (err) { toast.error("Error"); }
  };

  const handleSaveGaleriaHtml = async () => {
    if (!galeriaHtmlForm.titulo || !galeriaHtmlForm.contenido_html) return toast.error("Complete título y contenido HTML");
    setLoading(true);
    try {
      if (editingGaleriaHtml) {
        await axios.put(`${API}/informaciones/galeria-html/${editingGaleriaHtml.id}`, { ...galeriaHtmlForm, album_id: selectedAlbum.id }, getAuthHeaders());
        toast.success("Publicación HTML actualizada");
      } else {
        await axios.post(`${API}/informaciones/galeria-html`, { ...galeriaHtmlForm, album_id: selectedAlbum.id }, getAuthHeaders());
        toast.success("Publicación HTML creada");
      }
      setShowGaleriaHtmlDialog(false);
      setEditingGaleriaHtml(null);
      setGaleriaHtmlForm({ titulo: "", descripcion: "", album_id: "", contenido_html: "" });
      setGaleriaHtmlPreview(false);
      fetchFotos(selectedAlbum.id);
      fetchAlbumes();
    } catch (err) { toast.error("Error"); }
    finally { setLoading(false); }
  };

  // ============ CAROUSEL HANDLERS ============
  const handleCreateCarousel = async () => {
    if (!carouselFiles.length) return toast.error("Seleccione al menos una imagen");
    if (!carouselForm.titulo) return toast.error("Ingrese un título");
    setUploadingCarousel(true);
    try {
      const fd = new FormData();
      fd.append("titulo", carouselForm.titulo);
      fd.append("descripcion", carouselForm.descripcion);
      fd.append("efecto_transicion", carouselForm.efecto_transicion || "fade");
      for (const file of carouselFiles) fd.append("files", file);
      await axios.post(`${API}/informaciones/albumes/${selectedAlbum.id}/carousels`, fd, {
        ...getAuthHeaders(), headers: { ...getAuthHeaders().headers, "Content-Type": "multipart/form-data" }
      });
      toast.success("Carrusel creado");
      setShowCarouselDialog(false);
      setCarouselForm({ titulo: "", descripcion: "" });
      setCarouselFiles([]);
      fetchCarousels(selectedAlbum.id);
    } catch (err) { toast.error("Error al crear carrusel"); }
    finally { setUploadingCarousel(false); }
  };

  const handleDeleteCarousel = async (id) => {
    if (!window.confirm("¿Eliminar este carrusel?")) return;
    try {
      await axios.delete(`${API}/informaciones/carousels/${id}`, getAuthHeaders());
      toast.success("Carrusel eliminado");
      fetchCarousels(selectedAlbum.id);
    } catch (err) { toast.error("Error"); }
  };

  const handleChangeEfecto = async (carouselId, efecto) => {
    try {
      await axios.patch(`${API}/informaciones/carousels/${carouselId}/efecto`, { efecto_transicion: efecto }, getAuthHeaders());
      setCarousels(prev => prev.map(c => c.id === carouselId ? { ...c, efecto_transicion: efecto } : c));
      toast.success(`Efecto cambiado a: ${EFECTOS.find(e => e.value === efecto)?.label || efecto}`);
    } catch { toast.error("Error al cambiar efecto"); }
  };

  // ============ LIBRO VIEW COUNTER ============
  const handleViewLibro = async (libro) => {
    setViewingLibro(libro);
    try {
      const res = await axios.post(`${API}/informaciones/libros/${libro.id}/view`, {}, getAuthHeaders());
      setLibros(prev => prev.map(l => l.id === libro.id ? { ...l, view_count: res.data.view_count } : l));
    } catch (err) { console.error("Error registering view:", err); }
  };

  const handleUploadPdf = async (file) => {
    if (!file) return;
    setUploadingPdf(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const res = await axios.post(`${API}/informaciones/upload-pdf`, fd, {
        ...getAuthHeaders(), headers: { ...getAuthHeaders().headers, "Content-Type": "multipart/form-data" }
      });
      setLibroForm(prev => ({ ...prev, pdf_url: res.data.pdf_url }));
      toast.success(`PDF "${res.data.filename}" subido`);
    } catch (err) { toast.error(err.response?.data?.detail || "Error al subir PDF"); }
    finally { setUploadingPdf(false); }
  };

  // ============ DOCUMENTOS HANDLERS ============
  const handleCreateCat = async () => {
    if (!catForm.nombre) return toast.error("Ingrese nombre de la carpeta");
    try {
      if (editingCat) {
        await axios.put(`${API}/informaciones/doc-categorias/${editingCat.id}`, { nombre: catForm.nombre }, getAuthHeaders());
        toast.success("Carpeta actualizada");
      } else {
        await axios.post(`${API}/informaciones/doc-categorias`, { ...catForm, parent_id: catParentId || null }, getAuthHeaders());
        toast.success(catParentId ? "Subcarpeta creada" : "Carpeta creada");
      }
      setShowCatDialog(false);
      setCatForm({ nombre: "", descripcion: "" });
      setEditingCat(null);
      setCatParentId(null);
      fetchCategorias();
    } catch (err) { toast.error(err.response?.data?.detail || "Error"); }
  };

  const handleDeleteCat = async (cat) => {
    if (!window.confirm(`¿Eliminar la carpeta "${cat.nombre}" con sus subcarpetas y documentos? Esta acción no se puede deshacer.`)) return;
    try {
      await axios.delete(`${API}/informaciones/doc-categorias/${cat.id}`, getAuthHeaders());
      toast.success("Carpeta eliminada");
      if (selectedCat?.id === cat.id || selectedCat?.parent_id === cat.id) { setSelectedCat(null); fetchDocumentos(); }
      fetchCategorias();
    } catch (err) { toast.error(err.response?.data?.detail || "Error"); }
  };

  const MAX_DOC_FILES = 50;
  const MAX_DOC_MB = 50;
  const agregarDocFiles = (fileList) => {
    const nuevos = Array.from(fileList || []);
    if (!nuevos.length) return;
    const validos = nuevos.filter(f => {
      if (f.size > MAX_DOC_MB * 1024 * 1024) {
        toast.error(`"${f.name}" supera el límite de ${MAX_DOC_MB} MB`);
        return false;
      }
      return true;
    });
    setDocFiles(prev => {
      const total = [...prev, ...validos.map(f => ({ file: f, titulo: f.name.includes(".") ? f.name.slice(0, f.name.lastIndexOf(".")) : f.name }))];
      if (total.length > MAX_DOC_FILES) {
        toast.error(`Máximo ${MAX_DOC_FILES} archivos a la vez`);
        return total.slice(0, MAX_DOC_FILES);
      }
      return total;
    });
  };

  const CHUNK_DOC = 4 * 1024 * 1024;
  const subirArchivoEnBloques = async (item) => {
    const r = await axios.post(`${API}/informaciones/documentos/upload/iniciar`, {}, getAuthHeaders());
    const uploadId = r.data.upload_id;
    const total = Math.ceil(item.file.size / CHUNK_DOC);
    for (let c = 0; c < total; c++) {
      const fd = new FormData();
      fd.append("index", String(c));
      fd.append("file", item.file.slice(c * CHUNK_DOC, (c + 1) * CHUNK_DOC), `chunk_${c}`);
      await axios.post(`${API}/informaciones/documentos/upload/${uploadId}/chunk`, fd, getAuthHeaders());
    }
    const fd = new FormData();
    fd.append("filename", item.file.name);
    fd.append("content_type", item.file.type || "application/octet-stream");
    fd.append("titulo", (item.titulo || "").trim());
    fd.append("categoria_id", docForm.categoria_id);
    fd.append("descripcion", docForm.descripcion || "");
    await axios.post(`${API}/informaciones/documentos/upload/${uploadId}/completar`, fd, getAuthHeaders());
  };

  const handleUploadDoc = async () => {
    if (!docForm.categoria_id) return toast.error("Seleccione la carpeta destino");
    if (docFiles.length === 0) return toast.error("Agregue al menos un archivo");
    setUploadingDoc(true);
    let subidos = 0, fallidos = 0;
    for (let i = 0; i < docFiles.length; i++) {
      setUploadDocProgress(`${i + 1}/${docFiles.length}`);
      try {
        await subirArchivoEnBloques(docFiles[i]);
        subidos++;
      } catch (err) {
        fallidos++;
        const msg = err?.response?.data?.detail;
        if (msg) toast.error(`${docFiles[i].file.name}: ${msg}`);
      }
    }
    setUploadDocProgress("");
    if (subidos) toast.success(`${subidos} documento${subidos === 1 ? "" : "s"} subido${subidos === 1 ? "" : "s"}`);
    if (fallidos) toast.error(`${fallidos} archivo${fallidos === 1 ? "" : "s"} no se pudo subir`);
    setShowDocDialog(false);
    setDocForm({ titulo: "", descripcion: "", categoria_id: "" });
    setDocFiles([]);
    setDocFile(null);
    fetchDocumentos(selectedCat?.id);
    fetchCategorias();
    setUploadingDoc(false);
  };

  const handleDeleteDoc = async (id) => {
    if (!window.confirm("¿Eliminar este documento?")) return;
    try {
      await axios.delete(`${API}/informaciones/documentos/${id}`, getAuthHeaders());
      toast.success("Eliminado");
      fetchDocumentos(selectedCat?.id);
      fetchCategorias();
    } catch (err) { toast.error("Error"); }
  };

  const handleSaveHtmlDoc = async () => {
    if (!htmlDocForm.titulo || !htmlDocForm.categoria_id || !htmlDocForm.contenido_html) return toast.error("Complete título, categoría y contenido HTML");
    setLoading(true);
    try {
      if (editingHtmlDoc) {
        await axios.put(`${API}/informaciones/documentos-html/${editingHtmlDoc.id}`, htmlDocForm, getAuthHeaders());
        toast.success("Documento HTML actualizado");
      } else {
        await axios.post(`${API}/informaciones/documentos-html`, htmlDocForm, getAuthHeaders());
        toast.success("Documento HTML creado");
      }
      setShowHtmlDocDialog(false);
      setEditingHtmlDoc(null);
      setHtmlDocForm({ titulo: "", descripcion: "", categoria_id: "", contenido_html: "" });
      setHtmlPreviewMode(false);
      fetchDocumentos(selectedCat?.id);
      fetchCategorias();
    } catch (err) { toast.error("Error"); }
    finally { setLoading(false); }
  };

  // ============ LECTURA HANDLERS ============
  const handleSaveLibro = async () => {
    if (!libroForm.titulo || !libroForm.autor) return toast.error("Complete título y autor");
    setLoading(true);
    try {
      if (editingLibro) {
        await axios.put(`${API}/informaciones/libros/${editingLibro.id}`, libroForm, getAuthHeaders());
        if (libroPortadaFile) {
          const fd = new FormData();
          fd.append("file", libroPortadaFile);
          await axios.post(`${API}/informaciones/libros/${editingLibro.id}/portada`, fd, {
            ...getAuthHeaders(), headers: { ...getAuthHeaders().headers, "Content-Type": "multipart/form-data" }
          });
        }
        toast.success("Libro actualizado");
      } else {
        const res = await axios.post(`${API}/informaciones/libros`, libroForm, getAuthHeaders());
        if (libroPortadaFile) {
          const fd = new FormData();
          fd.append("file", libroPortadaFile);
          await axios.post(`${API}/informaciones/libros/${res.data.id}/portada`, fd, {
            ...getAuthHeaders(), headers: { ...getAuthHeaders().headers, "Content-Type": "multipart/form-data" }
          });
        }
        toast.success("Libro agregado");
      }
      setShowLibroDialog(false);
      setEditingLibro(null);
      setLibroForm({ titulo: "", autor: "", descripcion: "", enlace_externo: "", contenido_html: "" });
      setLibroPortadaFile(null);
      setLibroHtmlPreview(false);
      fetchLibros();
    } catch (err) { toast.error("Error"); }
    finally { setLoading(false); }
  };

  const handleDeleteLibro = async (id) => {
    if (!window.confirm("¿Eliminar este libro?")) return;
    try {
      await axios.delete(`${API}/informaciones/libros/${id}`, getAuthHeaders());
      toast.success("Eliminado");
      fetchLibros();
    } catch (err) { toast.error("Error"); }
  };

  // ============ DIRECTORIO HANDLERS ============
  const canManageDirectorio = user?.role?.toLowerCase() === "admin" || user?.role?.toLowerCase() === "administrador" || user?.role?.toLowerCase() === "gestion_humana";

  const handleSaveDir = async () => {
    if (!dirForm.nombre) return toast.error("El nombre es obligatorio");
    setLoading(true);
    try {
      if (editingDir) {
        await axios.put(`${API}/informaciones/directorio/${editingDir.id}`, dirForm, getAuthHeaders());
        toast.success("Registro actualizado");
      } else {
        await axios.post(`${API}/informaciones/directorio`, dirForm, getAuthHeaders());
        toast.success("Registro creado");
      }
      setShowDirDialog(false);
      setEditingDir(null);
      setDirForm({ nombre: "", extension: "", numero_directo: "", telefono: "", correo: "", departamento: "", cargo: "" });
      fetchDirectorio();
    } catch (err) { toast.error(err.response?.data?.detail || "Error"); }
    finally { setLoading(false); }
  };

  const handleDeleteDir = async (id) => {
    if (!window.confirm("¿Eliminar este registro?")) return;
    try {
      await axios.delete(`${API}/informaciones/directorio/${id}`, getAuthHeaders());
      toast.success("Eliminado");
      fetchDirectorio();
    } catch (err) { toast.error(err.response?.data?.detail || "Error"); }
  };

  const handleDownloadDirPDF = async () => {
    try {
      const res = await axios.get(`${API}/informaciones/directorio/pdf`, { ...getAuthHeaders(), responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = "Directorio_QAsesores.pdf";
      a.click();
      window.URL.revokeObjectURL(url);
      toast.success("PDF descargado");
    } catch (err) { toast.error("Error al generar PDF"); }
  };

  const handleSeedDirectorio = async () => {
    if (!window.confirm("¿Importar los datos del directorio desde el Excel original?")) return;
    try {
      const res = await axios.post(`${API}/informaciones/directorio/seed`, {}, getAuthHeaders());
      toast.success(res.data.message);
      fetchDirectorio();
    } catch (err) { toast.error(err.response?.data?.detail || "Error"); }
  };

  // ============ FILTERED DATA ============
  const sortedPublicaciones = [...publicaciones]
    .filter(p => !pubSearch || p.titulo.toLowerCase().includes(pubSearch.toLowerCase()) || p.contenido.toLowerCase().includes(pubSearch.toLowerCase()))
    .sort((a, b) => (b.fijada ? 1 : 0) - (a.fijada ? 1 : 0));

  // ============ LAYOUT HELPERS ============
  const applyLayoutOrder = (items, layout) => {
    if (!layout?.item_order?.length) return items;
    const orderMap = {};
    layout.item_order.forEach((id, idx) => { orderMap[id] = idx; });
    return [...items].sort((a, b) => (orderMap[a.id] ?? 9999) - (orderMap[b.id] ?? 9999));
  };

  const LSIZE = { full: "col-span-12", half: "col-span-12 md:col-span-6", third: "col-span-12 md:col-span-4" };

  const LayoutHeaderView = ({ tabId }) => {
    const h = tabLayouts[tabId]?.header;
    if (!h?.titulo) return null;
    return (
      <div className="rounded-xl overflow-hidden mb-6 shadow-lg" style={{ backgroundColor: h.bg_color || "#0d9488" }} data-testid={`layout-header-${tabId}`}>
        <div className="px-8 py-10 flex items-center gap-6" style={{ color: h.text_color || "#fff" }}>
          {h.logo_url && <img src={resolveFileUrl(h.logo_url)} alt="" className="w-16 h-16 rounded-xl object-cover bg-white/20 p-1" />}
          <div className="flex-1">
            <h1 className="text-3xl font-bold">{h.titulo}</h1>
            {h.subtitulo && <p className="text-lg opacity-80 mt-1">{h.subtitulo}</p>}
          </div>
          {h.buttons?.map((btn, i) => (
            <a key={i} href={btn.url} target="_blank" rel="noreferrer">
              <button className="px-5 py-2.5 rounded-lg font-medium text-sm transition-all hover:scale-105" style={{ backgroundColor: "rgba(255,255,255,0.2)", color: h.text_color, border: "1px solid rgba(255,255,255,0.3)" }}>
                {btn.label}
              </button>
            </a>
          ))}
        </div>
      </div>
    );
  };

  const LayoutFooterView = ({ tabId }) => {
    const f = tabLayouts[tabId]?.footer;
    if (!f?.texto && !f?.buttons?.length) return null;
    return (
      <div className="rounded-xl overflow-hidden mt-6 shadow-lg" style={{ backgroundColor: f.bg_color || "#1e293b" }} data-testid={`layout-footer-${tabId}`}>
        <div className="px-8 py-6 flex items-center justify-between" style={{ color: f.text_color || "#94a3b8" }}>
          <p className="text-sm">{f.texto}</p>
          <div className="flex gap-2">
            {f.buttons?.map((btn, i) => (
              <a key={i} href={btn.url} target="_blank" rel="noreferrer">
                <button className="px-4 py-2 rounded-lg text-sm transition-all hover:opacity-80" style={{ backgroundColor: "rgba(255,255,255,0.1)", color: f.text_color, border: "1px solid rgba(255,255,255,0.15)" }}>
                  {btn.label}
                </button>
              </a>
            ))}
          </div>
        </div>
      </div>
    );
  };

  const LayoutGridView = ({ items, tabId, renderItem }) => {
    const layout = tabLayouts[tabId];
    const ordered = applyLayoutOrder(items, layout);
    return (
      <div className="grid grid-cols-12 gap-4">
        {ordered.map(item => (
          <div key={item.id} className={`${LSIZE[layout?.item_sizes?.[item.id] || "full"]} transition-all`}>
            {renderItem(item)}
          </div>
        ))}
      </div>
    );
  };

  const hasLayout = (tabId) => !!(tabLayouts[tabId]?.item_order?.length > 0 || tabLayouts[tabId]?.header?.titulo || tabLayouts[tabId]?.footer?.texto);

  // ============ RENDER ITEM FUNCTIONS ============
  const renderNoticiaCard = (pub, showActions = true) => (
    <Card key={pub.id} data-item-id={pub.id} className={`overflow-hidden h-full ${pub.fijada ? "border-teal-300 dark:border-teal-700 bg-teal-50/30 dark:bg-teal-900/10" : ""} ${highlightedId === pub.id ? "ring-2 ring-teal-500 ring-offset-2" : ""} transition-all`} data-testid={`pub-${pub.id}`}>
      <div className="flex h-full">
        {/* Thumbnail */}
        <div className="w-36 flex-shrink-0 bg-gradient-to-br from-teal-50 to-cyan-50 dark:from-teal-900/20 dark:to-cyan-900/20 flex items-center justify-center overflow-hidden">
          {pub.imagen_url ? (
            <img src={resolveFileUrl(pub.imagen_url)} alt="" className="w-full h-full object-cover" />
          ) : (
            <Newspaper className="w-10 h-10 text-teal-300 dark:text-teal-700" />
          )}
        </div>
        <CardContent className="p-4 flex-1 min-w-0">
          <div className="flex items-start justify-between gap-2">
            <div className="min-w-0">
              <div className="flex items-center gap-2">
                {pub.fijada && <Pin className="w-4 h-4 text-teal-600 flex-shrink-0" />}
                <h3 className="text-base font-semibold text-slate-900 dark:text-white truncate" style={getTitleStyle("noticias")}>{pub.titulo}</h3>
              </div>
              <div className="flex items-center gap-2 mt-1 text-xs text-slate-500">
                <span>{new Date(pub.created_at).toLocaleDateString("es-ES", { day: "numeric", month: "long", year: "numeric" })}</span>
              </div>
            </div>
            {showActions && (
              <div className="flex gap-1 flex-shrink-0">
                <CopyLinkButton tab="noticias" itemId={pub.id} />
                {canPublish && (
                  <>
                    <Button variant="ghost" size="sm" onClick={() => handleTogglePin(pub.id)} title={pub.fijada ? "Desfijar" : "Fijar"}>
                      {pub.fijada ? <PinOff className="w-4 h-4" /> : <Pin className="w-4 h-4" />}
                    </Button>
                    <Button variant="ghost" size="sm" onClick={() => { setEditingPub(pub); setPubForm({ titulo: pub.titulo, contenido: pub.contenido, imagen_url: pub.imagen_url || "", fijada: pub.fijada, categoria: pub.categoria }); setShowPubDialog(true); }}>
                      <Edit className="w-4 h-4" />
                    </Button>
                    <Button variant="ghost" size="sm" className="text-red-500 hover:text-red-700" onClick={() => handleDeletePub(pub.id)}>
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </>
                )}
              </div>
            )}
          </div>
          <p className="mt-2 text-sm text-slate-600 dark:text-slate-300 whitespace-pre-wrap line-clamp-3">{pub.contenido}</p>
        </CardContent>
      </div>
    </Card>
  );

  const renderAlbumCard = (album) => (
    <Card key={album.id} data-item-id={album.id} className={`cursor-pointer hover:shadow-md transition-all overflow-hidden h-full group ${highlightedId === album.id ? "ring-2 ring-teal-500 ring-offset-2" : ""}`} onClick={() => { setSelectedAlbum(album); fetchFotos(album.id); fetchCarousels(album.id); }} data-testid={`album-${album.id}`}>
      <div className="aspect-video bg-slate-100 dark:bg-slate-800 flex items-center justify-center relative overflow-hidden">
        {album.cover_url ? (
          <img src={resolveFileUrl(album.cover_url)} alt="" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" />
        ) : (
          <Image className="w-12 h-12 text-slate-300" />
        )}
        <div className="absolute bottom-2 right-2 flex gap-1">
          {album.foto_count > 0 && (
            <span className="bg-black/60 text-white text-xs px-2 py-1 rounded-full backdrop-blur-sm">{album.foto_count} fotos</span>
          )}
        </div>
        <div className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity">
          <CopyLinkButton tab="galeria" itemId={album.id} />
        </div>
      </div>
      <CardContent className="p-3">
        <h3 className="font-semibold text-slate-900 dark:text-white truncate" style={getTitleStyle("galeria")}>{album.nombre}</h3>
      </CardContent>
    </Card>
  );

  const renderLibroCard = (libro, showActions = true) => {
    const thumbUrl = libro.portada_url || (libro.contenido_html ? extractHtmlImage(libro.contenido_html) : null);
    return (
    <Card key={libro.id} data-item-id={libro.id} className={`overflow-hidden hover:shadow-md transition-all h-full ${highlightedId === libro.id ? "ring-2 ring-teal-500 ring-offset-2" : ""}`} data-testid={`libro-${libro.id}`}>
      <div className="flex h-full">
        <div className="w-28 flex-shrink-0 bg-gradient-to-br from-amber-50 to-amber-100 dark:from-amber-900/20 dark:to-amber-800/20 flex items-center justify-center overflow-hidden">
          {thumbUrl ? (
            <img src={resolveFileUrl(thumbUrl)} alt="" className="w-full h-full object-cover" />
          ) : (
            <BookOpen className="w-10 h-10 text-amber-300" />
          )}
        </div>
        <CardContent className="p-4 flex-1 min-w-0">
          <h3 className="font-semibold text-slate-900 dark:text-white line-clamp-2" style={getTitleStyle("lectura")}>{libro.titulo}</h3>
          {libro.descripcion && <p className="text-xs text-slate-500 mt-2 line-clamp-2">{libro.descripcion}</p>}
          <div className="flex items-center justify-between mt-3">
            <div className="flex items-center gap-2">
              <span className="flex items-center gap-1 text-xs text-slate-400" data-testid={`libro-views-${libro.id}`}>
                <Eye className="w-3 h-3" /> {libro.view_count || 0}
              </span>
            </div>
            <div className="flex gap-1 flex-wrap">
              <CopyLinkButton tab="lectura" itemId={libro.id} />
              {libro.pdf_url && <a href={resolveFileUrl(libro.pdf_url)} target="_blank" rel="noreferrer"><Button variant="outline" size="sm"><Download className="w-3 h-3 mr-1" /> PDF</Button></a>}
              {libro.contenido_html && <Button variant="outline" size="sm" onClick={() => handleViewLibro(libro)} data-testid={`view-libro-html-${libro.id}`}><Eye className="w-3 h-3 mr-1" /> Leer</Button>}
              {libro.enlace_externo && <a href={libro.enlace_externo} target="_blank" rel="noreferrer"><Button variant="outline" size="sm"><Eye className="w-3 h-3 mr-1" /> Enlace</Button></a>}
              {showActions && canPublish && (
                <>
                  <Button variant="ghost" size="sm" onClick={() => { setEditingLibro(libro); setLibroForm({ titulo: libro.titulo, autor: libro.autor, descripcion: libro.descripcion || "", enlace_externo: libro.enlace_externo || "", contenido_html: libro.contenido_html || "", pdf_url: libro.pdf_url || "" }); setLibroPortadaFile(null); setLibroHtmlPreview(false); setShowLibroDialog(true); }}>
                    <Edit className="w-3 h-3" />
                  </Button>
                  <Button variant="ghost" size="sm" className="text-red-500" onClick={() => handleDeleteLibro(libro.id)}>
                    <Trash2 className="w-3 h-3" />
                  </Button>
                </>
              )}
            </div>
          </div>
        </CardContent>
      </div>
    </Card>
    );
  };

  const renderDirectorioCard = () => null; // Not used for table view

  // Filtered directorio entries
  const filteredDirectorio = directorio.filter(e => {
    const matchSearch = !dirSearch || e.nombre?.toLowerCase().includes(dirSearch.toLowerCase()) || e.correo?.toLowerCase().includes(dirSearch.toLowerCase()) || e.cargo?.toLowerCase().includes(dirSearch.toLowerCase()) || e.extension?.toLowerCase().includes(dirSearch.toLowerCase()) || e.numero_directo?.toLowerCase().includes(dirSearch.toLowerCase());
    const matchDept = !dirFilterDept || e.departamento === dirFilterDept;
    return matchSearch && matchDept;
  });

  // Group by department
  // Group by department with Socios - Directores first
  const directorioDepts = (() => {
    const allDepts = [...new Set(directorio.map(e => e.departamento).filter(Boolean))].sort();
    const priority = "Socios - Directores";
    return allDepts.includes(priority) ? [priority, ...allDepts.filter(d => d !== priority)] : allDepts;
  })();
  const groupedDirectorio = (() => {
    const groups = {};
    filteredDirectorio.forEach(e => {
      const dept = e.departamento || "Sin Departamento";
      if (!groups[dept]) groups[dept] = [];
      groups[dept].push(e);
    });
    // Order: Socios - Directores first, then alphabetical
    const ordered = {};
    const priority = "Socios - Directores";
    if (groups[priority]) ordered[priority] = groups[priority];
    Object.keys(groups).sort().forEach(k => { if (k !== priority) ordered[k] = groups[k]; });
    return ordered;
  })();

  // Extract first image URL from HTML content
  const extractHtmlImage = (html) => {
    if (!html) return null;
    const match = html.match(/<img[^>]+src=["']([^"']+)["']/i);
    return match ? match[1] : null;
  };

  const renderDocCard = (doc, showActions = true) => {
    const thumbUrl = doc.tipo === "html" ? extractHtmlImage(doc.contenido_html) : doc.thumbnail_url;
    const isPdf = doc.tipo_archivo === "PDF" || (doc.url && doc.url.toLowerCase().endsWith(".pdf"));
    return (
    <Card key={doc.id} data-item-id={doc.id} className={`hover:shadow-md transition-all h-full overflow-hidden ${highlightedId === doc.id ? "ring-2 ring-teal-500 ring-offset-2" : ""}`} data-testid={`doc-${doc.id}`}>
      <div className="flex h-full">
        <div className={`w-32 h-32 flex-shrink-0 flex items-center justify-center overflow-hidden cursor-pointer ${doc.tipo === "html" ? "bg-gradient-to-br from-orange-50 to-amber-50 dark:from-orange-900/20 dark:to-amber-900/20" : "bg-gradient-to-br from-blue-50 to-indigo-50 dark:from-blue-900/20 dark:to-indigo-900/20"}`}
          onClick={() => { if (doc.tipo === "html") setViewingHtmlDoc(doc); else if (isPdf && doc.url) openPdfViewer(doc); }}
        >
          {thumbUrl ? (
            <img src={resolveFileUrl(thumbUrl)} alt="" className="w-full h-full object-cover" />
          ) : (
            <FileText className={`w-10 h-10 ${doc.tipo === "html" ? "text-orange-300" : "text-blue-300"}`} />
          )}
        </div>
        <CardContent className="p-4 flex-1 min-w-0 flex items-center gap-4">
          <div className="flex-1 min-w-0">
            <h4 className="font-medium text-slate-900 dark:text-white truncate" style={getTitleStyle("documentos")}>{doc.titulo}</h4>
            {doc.descripcion && <p className="text-xs text-slate-500 mt-0.5 truncate">{doc.descripcion}</p>}
            <div className="flex items-center gap-2 mt-1">
              <Badge variant="outline" className="text-[10px]">{doc.tipo_archivo || "PDF"}</Badge>
              <span className="text-xs text-slate-400">{new Date(doc.created_at).toLocaleDateString("es-ES")}</span>
            </div>
          </div>
          <div className="flex gap-1 flex-shrink-0">
            <CopyLinkButton tab="documentos" itemId={doc.id} />
            {doc.tipo === "html" ? (
              <Button variant="outline" size="sm" onClick={() => setViewingHtmlDoc(doc)} data-testid={`view-html-${doc.id}`}><Eye className="w-4 h-4 mr-1" /> Ver</Button>
            ) : isPdf && doc.url ? (
              <>
                <Button variant="outline" size="sm" onClick={() => openPdfViewer(doc)} data-testid={`view-pdf-${doc.id}`}><Eye className="w-4 h-4 mr-1" /> Ver</Button>
                <a href={resolveFileUrl(doc.url)} download><Button variant="ghost" size="sm" title="Descargar"><Download className="w-4 h-4" /></Button></a>
              </>
            ) : doc.url ? (
              <a href={resolveFileUrl(doc.url)} target="_blank" rel="noreferrer"><Button variant="outline" size="sm"><Download className="w-4 h-4 mr-1" /> Descargar</Button></a>
            ) : (
              <Badge variant="outline" className="text-[10px] text-slate-400 self-center" data-testid={`doc-sin-archivo-${doc.id}`}>Sin archivo adjunto</Badge>
            )}
            {showActions && canPublish && doc.tipo === "html" && (
              <Button variant="ghost" size="sm" onClick={() => { setEditingHtmlDoc(doc); setHtmlDocForm({ titulo: doc.titulo, descripcion: doc.descripcion || "", categoria_id: doc.categoria_id, contenido_html: doc.contenido_html || "" }); setHtmlPreviewMode(false); setShowHtmlDocDialog(true); }}>
                <Edit className="w-4 h-4" />
              </Button>
            )}
            {showActions && canPublish && (
              <Button variant="ghost" size="sm" className="text-red-500" onClick={() => handleDeleteDoc(doc.id)}>
                <Trash2 className="w-4 h-4" />
              </Button>
            )}
          </div>
        </CardContent>
      </div>
    </Card>
    );
  };

  return (
    <div className="flex min-h-screen bg-slate-50 dark:bg-slate-900">
      <Sidebar />
      <main className="flex-1 ml-0 md:ml-64">
    <div className="p-4 md:p-6 max-w-7xl mx-auto">
      <div className="mb-6">
        <h1 className="text-2xl md:text-3xl font-bold text-slate-900 dark:text-white flex items-center gap-3">
          <Building2 className="w-7 h-7 text-teal-600" />
          Informaciones Generales
        </h1>
        <p className="text-slate-500 dark:text-slate-400 mt-1">Portal interno de información y recursos de la empresa</p>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="flex flex-wrap gap-1 mb-6 bg-slate-100 dark:bg-slate-800 p-1 rounded-lg">
          <TabsTrigger value="noticias" className="gap-2 data-[state=active]:bg-white dark:data-[state=active]:bg-slate-700">
            <Newspaper className="w-4 h-4" /> Noticias
          </TabsTrigger>
          <TabsTrigger value="galeria" className="gap-2 data-[state=active]:bg-white dark:data-[state=active]:bg-slate-700">
            <Image className="w-4 h-4" /> Galería
          </TabsTrigger>
          <TabsTrigger value="documentos" className="gap-2 data-[state=active]:bg-white dark:data-[state=active]:bg-slate-700">
            <FolderOpen className="w-4 h-4" /> Documentos
          </TabsTrigger>
          <TabsTrigger value="lectura" className="gap-2 data-[state=active]:bg-white dark:data-[state=active]:bg-slate-700">
            <BookOpen className="w-4 h-4" /> Rincón de Lectura
          </TabsTrigger>
          <TabsTrigger value="directorio" className="gap-2 data-[state=active]:bg-white dark:data-[state=active]:bg-slate-700">
            <Users className="w-4 h-4" /> Directorio
          </TabsTrigger>
        </TabsList>

        {/* ============ NOTICIAS TAB ============ */}
        <TabsContent value="noticias">
          {layoutMode === "noticias" ? (
            <TabLayoutEditor
              tabId="noticias"
              items={sortedPublicaciones}
              renderItem={(item, isPreview) => renderNoticiaCard(item, !isPreview)}
              onBack={() => { setLayoutMode(null); fetchTabLayout("noticias"); }}
            />
          ) : (
            <>
              <div className="flex items-center justify-between mb-4 gap-4">
                <div className="relative flex-1 max-w-sm">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <Input placeholder="Buscar noticias..." value={pubSearch} onChange={e => setPubSearch(e.target.value)} className="pl-10" data-testid="noticias-search" />
                </div>
                <div className="flex gap-2">
                  {canPublish && (
                    <Button variant="outline" size="sm" onClick={() => setLayoutMode("noticias")} data-testid="layout-noticias-btn">
                      <LayoutGrid className="w-4 h-4 mr-2" /> Personalizar
                    </Button>
                  )}
                  {canPublish && (
                    <Button onClick={() => { setEditingPub(null); setPubForm({ titulo: "", contenido: "", imagen_url: "", fijada: false, categoria: "general" }); setShowPubDialog(true); }} className="bg-teal-600 hover:bg-teal-700 text-white" data-testid="new-pub-btn">
                      <Plus className="w-4 h-4 mr-2" /> Nueva Publicación
                    </Button>
                  )}
                </div>
              </div>
              <LayoutHeaderView tabId="noticias" />
              {sortedPublicaciones.length === 0 ? (
                <Card><CardContent className="py-12 text-center text-slate-500">No hay publicaciones</CardContent></Card>
              ) : hasLayout("noticias") ? (
                <LayoutGridView items={sortedPublicaciones} tabId="noticias" renderItem={(pub) => renderNoticiaCard(pub, true)} />
              ) : (
                <div className="space-y-4">
                  {sortedPublicaciones.map(pub => renderNoticiaCard(pub, true))}
                </div>
              )}
              <LayoutFooterView tabId="noticias" />
            </>
          )}
        </TabsContent>

        {/* ============ GALERÍA TAB ============ */}
        <TabsContent value="galeria">
          {layoutMode === "galeria" ? (
            <TabLayoutEditor
              tabId="galeria"
              items={albumes}
              renderItem={(item, isPreview) => renderAlbumCard(item)}
              onBack={() => { setLayoutMode(null); fetchTabLayout("galeria"); }}
            />
          ) : selectedAlbum ? (
            <div>
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-3">
                  <Button variant="outline" size="sm" onClick={() => { setSelectedAlbum(null); setFotos([]); setCarousels([]); }}>
                    <ChevronLeft className="w-4 h-4 mr-1" /> Volver
                  </Button>
                  <h2 className="text-xl font-bold text-slate-900 dark:text-white">{selectedAlbum.nombre}</h2>
                  <Badge variant="outline">{fotos.length} fotos</Badge>
                  {carousels.length > 0 && <Badge variant="outline" className="bg-teal-50 text-teal-700 border-teal-200">{carousels.length} carrusel(es)</Badge>}
                </div>
                {canPublish && (
                  <div className="flex gap-2">
                    <Button variant="outline" size="sm" onClick={() => { setCarouselForm({ titulo: "", descripcion: "" }); setCarouselFiles([]); setShowCarouselDialog(true); }} data-testid="new-carousel-btn">
                      <Images className="w-4 h-4 mr-1" /> Carrusel
                    </Button>
                    <Button variant="outline" size="sm" onClick={() => { setEditingGaleriaHtml(null); setGaleriaHtmlForm({ titulo: "", descripcion: "", album_id: selectedAlbum.id, contenido_html: "" }); setGaleriaHtmlPreview(false); setShowGaleriaHtmlDialog(true); }} data-testid="new-galeria-html-btn">
                      <FileText className="w-4 h-4 mr-1" /> HTML
                    </Button>
                    <label className="cursor-pointer">
                      <input type="file" multiple accept="image/*" className="hidden" onChange={handleUploadFoto} />
                      <Button asChild disabled={uploadingFoto}>
                        <span>{uploadingFoto ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Upload className="w-4 h-4 mr-2" />} Subir Fotos</span>
                      </Button>
                    </label>
                    <Button variant="destructive" size="sm" onClick={() => handleDeleteAlbum(selectedAlbum.id)}>
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                )}
              </div>

              {/* Carruseles */}
              {carousels.length > 0 && (
                <div className="mb-6 space-y-4">
                  {carousels.map(carousel => (
                    <CarouselCard key={carousel.id} carousel={carousel} canPublish={canPublish} onDelete={() => handleDeleteCarousel(carousel.id)} onImageClick={setLightboxFoto} titleStyle={getTitleStyle("galeria")} onChangeEfecto={handleChangeEfecto} />
                  ))}
                </div>
              )}

              {/* Fotos individuales */}
              {fotos.length === 0 && carousels.length === 0 ? (
                <Card><CardContent className="py-12 text-center text-slate-500">No hay fotos en este álbum</CardContent></Card>
              ) : fotos.length > 0 && (
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-3">
                  {fotos.map(foto => (
                    foto.tipo === "html" ? (
                      <div key={foto.id} className="group relative aspect-square rounded-lg overflow-hidden border bg-slate-100 dark:bg-slate-800 cursor-pointer flex flex-col" onClick={() => setViewingGaleriaHtml(foto)}>
                        <div className="flex-1 overflow-hidden">
                          {extractHtmlImage(foto.contenido_html) ? (
                            <img src={extractHtmlImage(foto.contenido_html)} alt={foto.titulo} className="w-full h-full object-cover" />
                          ) : (
                            <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-orange-50 to-amber-50 dark:from-orange-900/20 dark:to-amber-900/20 p-2">
                              <iframe srcDoc={foto.contenido_html} className="w-full h-full border-0 pointer-events-none" title={foto.titulo} sandbox="" />
                            </div>
                          )}
                        </div>
                        <div className="p-2 bg-white/90 dark:bg-slate-800/90 border-t">
                          <div className="flex items-center gap-1">
                            <span className="text-xs font-medium truncate">{foto.titulo}</span>
                          </div>
                        </div>
                        {canPublish && (
                          <div className="absolute top-2 right-2 flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                            <button onClick={e => { e.stopPropagation(); setEditingGaleriaHtml(foto); setGaleriaHtmlForm({ titulo: foto.titulo, descripcion: foto.descripcion || "", album_id: foto.album_id, contenido_html: foto.contenido_html || "" }); setGaleriaHtmlPreview(false); setShowGaleriaHtmlDialog(true); }} className="bg-white text-slate-700 rounded-full p-1 shadow">
                              <Edit className="w-3 h-3" />
                            </button>
                            <button onClick={e => { e.stopPropagation(); handleDeleteFoto(foto.id); }} className="bg-red-500 text-white rounded-full p-1">
                              <Trash2 className="w-3 h-3" />
                            </button>
                          </div>
                        )}
                      </div>
                    ) : (
                      <div key={foto.id} className="group relative aspect-square rounded-lg overflow-hidden border bg-slate-100 dark:bg-slate-800 cursor-pointer" onClick={() => setLightboxFoto(foto)}>
                        <img src={resolveFileUrl(foto.url)} alt={foto.nombre} className="w-full h-full object-cover" />
                        {canPublish && (
                          <button onClick={e => { e.stopPropagation(); handleDeleteFoto(foto.id); }} className="absolute top-2 right-2 bg-red-500 text-white rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity">
                            <Trash2 className="w-3 h-3" />
                          </button>
                        )}
                      </div>
                    )
                  ))}
                </div>
              )}
            </div>
          ) : (
            <>
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-xl font-bold text-slate-900 dark:text-white">Álbumes de Fotos</h2>
                <div className="flex gap-2">
                  {canPublish && (
                    <Button variant="outline" size="sm" onClick={() => setLayoutMode("galeria")} data-testid="layout-galeria-btn">
                      <LayoutGrid className="w-4 h-4 mr-2" /> Personalizar
                    </Button>
                  )}
                  {canPublish && (
                    <Button onClick={() => setShowAlbumDialog(true)} className="bg-teal-600 hover:bg-teal-700 text-white" data-testid="new-album-btn">
                      <Plus className="w-4 h-4 mr-2" /> Nuevo Álbum
                    </Button>
                  )}
                </div>
              </div>
              <LayoutHeaderView tabId="galeria" />
              {albumes.length === 0 ? (
                <Card><CardContent className="py-12 text-center text-slate-500">No hay álbumes creados</CardContent></Card>
              ) : hasLayout("galeria") ? (
                <LayoutGridView items={albumes} tabId="galeria" renderItem={renderAlbumCard} />
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                  {albumes.map(album => renderAlbumCard(album))}
                </div>
              )}
              <LayoutFooterView tabId="galeria" />
            </>
          )}
        </TabsContent>

        {/* ============ DOCUMENTOS TAB ============ */}
        <TabsContent value="documentos">
          {layoutMode === "documentos" ? (
            <TabLayoutEditor
              tabId="documentos"
              items={documentos}
              renderItem={(item, isPreview) => renderDocCard(item, !isPreview)}
              onBack={() => { setLayoutMode(null); fetchTabLayout("documentos"); }}
            />
          ) : (
            <>
              <LayoutHeaderView tabId="documentos" />
              <div className="flex gap-6">
                {/* Categories sidebar */}
                <div className="w-64 flex-shrink-0">
                  <div className="flex items-center justify-between mb-3">
                    <h3 className="font-semibold text-slate-900 dark:text-white">Categorías</h3>
                    {canPublish && (
                      <Button variant="ghost" size="sm" onClick={() => setShowCatDialog(true)} data-testid="new-cat-btn">
                        <Plus className="w-4 h-4" />
                      </Button>
                    )}
                  </div>
                  <div className="space-y-1">
                    <button onClick={() => { setSelectedCat(null); fetchDocumentos(); }} className={`w-full text-left px-3 py-2 rounded-lg text-sm transition-colors ${!selectedCat ? "bg-teal-100 text-teal-700 dark:bg-teal-900/30 dark:text-teal-300" : "hover:bg-slate-100 dark:hover:bg-slate-800"}`}>
                      <div className="flex items-center justify-between">
                        <span>Todos</span>
                        <Badge variant="outline" className="text-xs">{categorias.reduce((sum, c) => sum + (c.doc_count || 0), 0)}</Badge>
                      </div>
                    </button>
                    {categorias.filter(c => !c.parent_id).map(cat => (
                      <div key={cat.id}>
                        {[cat, ...categorias.filter(s => s.parent_id === cat.id)].map((c, idx) => (
                          <div key={c.id} className={`group flex items-center gap-1 rounded-lg ${selectedCat?.id === c.id ? "bg-teal-100 dark:bg-teal-900/30" : "hover:bg-slate-100 dark:hover:bg-slate-800"}`} style={{ marginLeft: idx === 0 ? 0 : 16 }}>
                            <button onClick={() => { setSelectedCat(c); fetchDocumentos(c.id); }} className={`flex-1 min-w-0 text-left px-3 py-2 text-sm transition-colors ${selectedCat?.id === c.id ? "text-teal-700 dark:text-teal-300" : ""}`} data-testid={`cat-item-${c.id}`}>
                              <div className="flex items-center justify-between gap-1">
                                <span className="flex items-center gap-2 min-w-0">
                                  {idx === 0 ? <FolderOpen className="w-4 h-4 flex-shrink-0" /> : <Folder className="w-3.5 h-3.5 flex-shrink-0" />}
                                  <span className="truncate">{c.nombre}</span>
                                </span>
                                <Badge variant="outline" className="text-xs flex-shrink-0">{c.doc_count || 0}</Badge>
                              </div>
                            </button>
                            {isAdmin && (
                              <div className="hidden group-hover:flex items-center pr-1 flex-shrink-0">
                                <button title="Renombrar carpeta" data-testid={`edit-cat-${c.id}`} className="p-1 text-slate-400 hover:text-teal-600" onClick={() => { setEditingCat(c); setCatParentId(null); setCatForm({ nombre: c.nombre, descripcion: c.descripcion || "" }); setShowCatDialog(true); }}>
                                  <Pencil className="w-3.5 h-3.5" />
                                </button>
                                <button title="Eliminar carpeta" data-testid={`delete-cat-${c.id}`} className="p-1 text-slate-400 hover:text-red-600" onClick={() => handleDeleteCat(c)}>
                                  <Trash2 className="w-3.5 h-3.5" />
                                </button>
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    ))}
                  </div>
                </div>
                {/* Documents list */}
                <div className="flex-1"
                  onDragOver={(e) => { if (canPublish && selectedCat) e.preventDefault(); }}
                  onDrop={(e) => {
                    if (!canPublish || !selectedCat) return;
                    e.preventDefault();
                    if (e.dataTransfer?.files?.length) {
                      setDocForm({ titulo: "", descripcion: "", categoria_id: selectedCat.id });
                      setDocFiles([]);
                      agregarDocFiles(e.dataTransfer.files);
                      setShowDocDialog(true);
                    }
                  }}>
                  <div className="flex items-center justify-between mb-4">
                    <h2 className="text-xl font-bold text-slate-900 dark:text-white">
                      {selectedCat?.parent_id ? `${categorias.find(c => c.id === selectedCat.parent_id)?.nombre || ""} / ${selectedCat.nombre}` : (selectedCat?.nombre || "Todos los Documentos")}
                    </h2>
                    {canPublish && (
                      <div className="flex gap-2">
                        <Button variant="outline" size="sm" onClick={() => setLayoutMode("documentos")} data-testid="layout-documentos-btn">
                          <LayoutGrid className="w-4 h-4 mr-2" /> Personalizar
                        </Button>
                        {selectedCat && !selectedCat.parent_id && (
                          <Button variant="outline" onClick={() => { setEditingCat(null); setCatParentId(selectedCat.id); setCatForm({ nombre: "", descripcion: "" }); setShowCatDialog(true); }} data-testid="new-subcat-btn">
                            <Folder className="w-4 h-4 mr-2" /> Nueva Subcarpeta
                          </Button>
                        )}
                        <Button variant="outline" onClick={() => { setEditingHtmlDoc(null); setHtmlDocForm({ titulo: "", descripcion: "", categoria_id: selectedCat?.id || "", contenido_html: "" }); setHtmlPreviewMode(false); setShowHtmlDocDialog(true); }} data-testid="new-html-doc-btn">
                          <FileText className="w-4 h-4 mr-2" /> Publicación HTML
                        </Button>
                        <Button onClick={() => { setDocForm({ titulo: "", descripcion: "", categoria_id: selectedCat?.id || "" }); setDocFiles([]); setDocFile(null); setShowDocDialog(true); }} className="bg-teal-600 hover:bg-teal-700 text-white" data-testid="new-doc-btn">
                          <Plus className="w-4 h-4 mr-2" /> Subir Archivos
                        </Button>
                      </div>
                    )}
                  </div>
                  {selectedCat && categorias.filter(c => c.parent_id === selectedCat.id).length > 0 && (
                    <div className="mb-5">
                      <h4 className="text-sm font-semibold text-slate-500 mb-2">Carpetas</h4>
                      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2">
                        {categorias.filter(c => c.parent_id === selectedCat.id).map(sc => (
                          <button key={sc.id} onClick={() => { setSelectedCat(sc); fetchDocumentos(sc.id); }}
                            className="flex items-center gap-2 p-3 rounded-lg border border-slate-200 dark:border-slate-700 hover:border-teal-400 hover:bg-teal-50 dark:hover:bg-teal-900/20 transition-colors text-left"
                            data-testid={`subcarpeta-card-${sc.id}`}>
                            <Folder className="w-5 h-5 text-teal-600 flex-shrink-0" />
                            <span className="text-sm font-medium truncate flex-1">{sc.nombre}</span>
                            <Badge variant="outline" className="text-xs">{sc.doc_count || 0}</Badge>
                          </button>
                        ))}
                      </div>
                    </div>
                  )}
                  {documentos.length > 0 && (
                    <div className="relative mb-4">
                      <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                      <Input
                        data-testid="doc-search-input"
                        value={docSearch}
                        onChange={(e) => setDocSearch(e.target.value)}
                        placeholder="Buscar documento por nombre o descripción..."
                        className="pl-9"
                      />
                    </div>
                  )}
                  {(() => {
                    const norm = (s) => String(s || "").normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase();
                    const q = norm(docSearch.trim());
                    const docsFiltrados = q
                      ? documentos.filter(d => norm(d.titulo).includes(q) || norm(d.descripcion).includes(q) || norm(d.nombre_archivo).includes(q))
                      : documentos;
                    if (documentos.length === 0) {
                      return (
                        <Card><CardContent className="py-12 text-center text-slate-500">
                          {canPublish && selectedCat ? "No hay documentos. Arrastra y suelta archivos aquí para subirlos." : "No hay documentos"}
                        </CardContent></Card>
                      );
                    }
                    if (docsFiltrados.length === 0) {
                      return (
                        <Card><CardContent className="py-12 text-center text-slate-500" data-testid="doc-search-empty">
                          Sin resultados para "{docSearch.trim()}"
                        </CardContent></Card>
                      );
                    }
                    if (!q && hasLayout("documentos")) {
                      return <LayoutGridView items={documentos} tabId="documentos" renderItem={(doc) => renderDocCard(doc, true)} />;
                    }
                    return (
                      <div className="space-y-2">
                        {docsFiltrados.map(doc => renderDocCard(doc, true))}
                      </div>
                    );
                  })()}
                </div>
              </div>
              <LayoutFooterView tabId="documentos" />
            </>
          )}
        </TabsContent>

        {/* ============ LECTURA TAB ============ */}
        <TabsContent value="lectura">
          {layoutMode === "lectura" ? (
            <TabLayoutEditor
              tabId="lectura"
              items={libros}
              renderItem={(item, isPreview) => renderLibroCard(item, !isPreview)}
              onBack={() => { setLayoutMode(null); fetchTabLayout("lectura"); }}
            />
          ) : (
            <>
              <div className="flex items-center justify-between mb-4">
                <div>
                  <h2 className="text-xl font-bold text-slate-900 dark:text-white">Rincón de Lectura</h2>
                  <p className="text-sm text-slate-500">Recomendaciones de libros para el equipo</p>
                </div>
                <div className="flex gap-2">
                  {canPublish && (
                    <Button variant="outline" size="sm" onClick={() => setLayoutMode("lectura")} data-testid="layout-lectura-btn">
                      <LayoutGrid className="w-4 h-4 mr-2" /> Personalizar
                    </Button>
                  )}
                  {canPublish && (
                    <Button onClick={() => { setEditingLibro(null); setLibroForm({ titulo: "", autor: "", descripcion: "", enlace_externo: "", contenido_html: "" }); setLibroPortadaFile(null); setLibroHtmlPreview(false); setShowLibroDialog(true); }} className="bg-teal-600 hover:bg-teal-700 text-white" data-testid="new-libro-btn">
                      <Plus className="w-4 h-4 mr-2" /> Agregar Libro
                    </Button>
                  )}
                </div>
              </div>
              <LayoutHeaderView tabId="lectura" />
              {libros.length === 0 ? (
                <Card><CardContent className="py-12 text-center text-slate-500">No hay recomendaciones de libros</CardContent></Card>
              ) : hasLayout("lectura") ? (
                <LayoutGridView items={libros} tabId="lectura" renderItem={(libro) => renderLibroCard(libro, true)} />
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                  {libros.map(libro => renderLibroCard(libro, true))}
                </div>
              )}
              <LayoutFooterView tabId="lectura" />
            </>
          )}
        </TabsContent>

        {/* ============ DIRECTORIO TAB ============ */}
        <TabsContent value="directorio">
          <div className="space-y-4">
            {/* Header */}
            <div className="flex items-center justify-between gap-4 flex-wrap">
              <div>
                <h2 className="text-xl font-bold text-slate-900 dark:text-white">Directorio de Personal</h2>
                <p className="text-sm text-slate-500">Extensiones, flotas y correos del personal de QAsesores</p>
              </div>
              <div className="flex gap-2 flex-wrap">
                <Button variant="outline" size="sm" onClick={handleDownloadDirPDF} data-testid="download-dir-pdf">
                  <Download className="w-4 h-4 mr-2" /> Descargar PDF
                </Button>
                {canManageDirectorio && directorio.length === 0 && (
                  <Button variant="outline" size="sm" onClick={handleSeedDirectorio} data-testid="seed-dir-btn">
                    <Upload className="w-4 h-4 mr-2" /> Importar Datos
                  </Button>
                )}
                {canManageDirectorio && (
                  <Button onClick={() => { setEditingDir(null); setDirForm({ nombre: "", extension: "", numero_directo: "", telefono: "", correo: "", departamento: "", cargo: "" }); setShowDirDialog(true); }} className="bg-teal-600 hover:bg-teal-700 text-white" size="sm" data-testid="new-dir-btn">
                    <Plus className="w-4 h-4 mr-2" /> Nuevo Registro
                  </Button>
                )}
              </div>
            </div>

            {/* Filters */}
            <div className="flex gap-3 flex-wrap">
              <div className="relative flex-1 max-w-sm">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input placeholder="Buscar por nombre, correo o cargo..." value={dirSearch} onChange={e => setDirSearch(e.target.value)} className="pl-10" data-testid="dir-search" />
              </div>
              <Select value={dirFilterDept} onValueChange={v => setDirFilterDept(v === "all" ? "" : v)}>
                <SelectTrigger className="w-56" data-testid="dir-filter-dept">
                  <SelectValue placeholder="Todos los departamentos" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todos los departamentos</SelectItem>
                  {directorioDepts.map(d => <SelectItem key={d} value={d}>{d}</SelectItem>)}
                </SelectContent>
              </Select>
              <Badge variant="outline" className="self-center">{filteredDirectorio.length} de {directorio.length}</Badge>
            </div>

            {/* Table by department */}
            {directorio.length === 0 ? (
              <Card><CardContent className="py-12 text-center text-slate-500">
                No hay registros en el directorio.
                {canManageDirectorio && <Button variant="link" className="text-teal-600 ml-2" onClick={handleSeedDirectorio}>Importar datos iniciales</Button>}
              </CardContent></Card>
            ) : filteredDirectorio.length === 0 ? (
              <Card><CardContent className="py-8 text-center text-slate-500">No se encontraron resultados</CardContent></Card>
            ) : (
              Object.entries(groupedDirectorio).map(([dept, members]) => (
                <div key={dept} data-testid={`dept-group-${dept}`}>
                  <div className="flex items-center gap-3 mb-2">
                    <div className="h-8 px-4 rounded-lg bg-gradient-to-r from-teal-600 to-cyan-600 flex items-center">
                      <span className="text-sm font-semibold text-white">{dept}</span>
                    </div>
                  </div>
                  <Card className="overflow-hidden mb-4">
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm table-fixed" data-testid={`dir-table-${dept}`} style={{minWidth: "900px"}}>
                        <thead>
                          <tr className="bg-slate-100 dark:bg-slate-800 border-b">
                            <th className="text-center px-3 py-2.5 font-semibold text-slate-700 dark:text-slate-300" style={{width: "18%"}}>Nombre</th>
                            <th className="text-center px-3 py-2.5 font-semibold text-slate-700 dark:text-slate-300" style={{width: "8%"}}>Extension</th>
                            <th className="text-center px-3 py-2.5 font-semibold text-slate-700 dark:text-slate-300" style={{width: "14%"}}>N. Directo</th>
                            <th className="text-center px-3 py-2.5 font-semibold text-slate-700 dark:text-slate-300" style={{width: "13%"}}>Flota</th>
                            <th className="text-center px-3 py-2.5 font-semibold text-slate-700 dark:text-slate-300" style={{width: "22%"}}>Correo</th>
                            <th className="text-center px-3 py-2.5 font-semibold text-slate-700 dark:text-slate-300" style={{width: "16%"}}>Cargo</th>
                            {canManageDirectorio && <th className="text-center px-3 py-2.5 font-semibold text-slate-700 dark:text-slate-300" style={{width: "9%"}}>Acciones</th>}
                          </tr>
                        </thead>
                        <tbody>
                          {members.map((entry, idx) => (
                            <tr key={entry.id} className={`border-b last:border-0 hover:bg-slate-50 dark:hover:bg-slate-800/50 transition-colors ${idx % 2 === 1 ? "bg-slate-50/50 dark:bg-slate-800/20" : ""}`} data-testid={`dir-row-${entry.id}`}>
                              <td className="px-3 py-2.5 font-medium text-slate-900 dark:text-white whitespace-nowrap overflow-hidden text-ellipsis">{entry.nombre}</td>
                              <td className="px-3 py-2.5 text-center text-slate-600 dark:text-slate-400 whitespace-nowrap">{entry.extension || "-"}</td>
                              <td className="px-3 py-2.5 text-center text-slate-600 dark:text-slate-400 whitespace-nowrap">{entry.numero_directo || "-"}</td>
                              <td className="px-3 py-2.5 text-center whitespace-nowrap">
                                {entry.telefono ? (
                                  <span className="inline-flex items-center gap-1.5 text-slate-600 dark:text-slate-400">
                                    <Phone className="w-3 h-3 text-teal-500 shrink-0" />{entry.telefono}
                                  </span>
                                ) : <span className="text-slate-400">-</span>}
                              </td>
                              <td className="px-3 py-2.5 text-center whitespace-nowrap overflow-hidden text-ellipsis">
                                {entry.correo ? (
                                  <a href={`mailto:${entry.correo}`} className="inline-flex items-center gap-1.5 text-teal-600 hover:text-teal-700 dark:text-teal-400 hover:underline">
                                    <Mail className="w-3 h-3 shrink-0" />{entry.correo}
                                  </a>
                                ) : <span className="text-slate-400">-</span>}
                              </td>
                              <td className="px-3 py-2.5 text-center text-slate-600 dark:text-slate-400 whitespace-nowrap overflow-hidden text-ellipsis">{entry.cargo || "-"}</td>
                              {canManageDirectorio && (
                                <td className="px-3 py-2.5 text-center">
                                  <div className="flex gap-1 justify-center">
                                    <Button variant="ghost" size="sm" onClick={() => { setEditingDir(entry); setDirForm({ nombre: entry.nombre, extension: entry.extension || "", numero_directo: entry.numero_directo || "", telefono: entry.telefono || "", correo: entry.correo || "", departamento: entry.departamento || "", cargo: entry.cargo || "" }); setShowDirDialog(true); }} data-testid={`edit-dir-${entry.id}`}>
                                      <Edit className="w-3.5 h-3.5" />
                                    </Button>
                                    <Button variant="ghost" size="sm" className="text-red-500 hover:text-red-700" onClick={() => handleDeleteDir(entry.id)} data-testid={`delete-dir-${entry.id}`}>
                                      <Trash2 className="w-3.5 h-3.5" />
                                    </Button>
                                  </div>
                                </td>
                              )}
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </Card>
                </div>
              ))
            )}
          </div>
        </TabsContent>
      </Tabs>

      {/* ============ DIALOGS ============ */}

      {/* Publicación Dialog */}
      <Dialog open={showPubDialog} onOpenChange={setShowPubDialog}>
        <DialogContent className="max-w-lg dark:bg-slate-800">
          <DialogHeader>
            <DialogTitle>{editingPub ? "Editar Publicación" : "Nueva Publicación"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div><Label>Título *</Label><Input value={pubForm.titulo} onChange={e => setPubForm({...pubForm, titulo: e.target.value})} data-testid="pub-titulo" /></div>
            <div><Label>Contenido *</Label><Textarea rows={5} value={pubForm.contenido} onChange={e => setPubForm({...pubForm, contenido: e.target.value})} data-testid="pub-contenido" /></div>
            <div>
              <Label>Categoría</Label>
              <Select value={pubForm.categoria} onValueChange={v => setPubForm({...pubForm, categoria: v})}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="general">General</SelectItem>
                  <SelectItem value="comunicado">Comunicado</SelectItem>
                  <SelectItem value="logro">Logro</SelectItem>
                  <SelectItem value="evento">Evento</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Imagen</Label>
              {pubForm.imagen_url && <img src={resolveFileUrl(pubForm.imagen_url)} alt="" className="w-full h-32 object-cover rounded-lg mb-2" />}
              <label className="cursor-pointer">
                <input type="file" accept="image/*" className="hidden" onChange={e => e.target.files[0] && handleUploadImage(e.target.files[0])} />
                <Button asChild variant="outline" disabled={uploadingImg}>
                  <span>{uploadingImg ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Upload className="w-4 h-4 mr-2" />} Subir Imagen</span>
                </Button>
              </label>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowPubDialog(false)}>Cancelar</Button>
            <Button onClick={handleSavePub} disabled={loading} className="bg-teal-600 hover:bg-teal-700 text-white" data-testid="save-pub-btn">
              {loading && <Loader2 className="w-4 h-4 mr-2 animate-spin" />} Guardar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Album Dialog */}
      <Dialog open={showAlbumDialog} onOpenChange={setShowAlbumDialog}>
        <DialogContent className="max-w-md dark:bg-slate-800">
          <DialogHeader><DialogTitle>Nuevo Álbum</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div><Label>Nombre *</Label><Input value={albumForm.nombre} onChange={e => setAlbumForm({...albumForm, nombre: e.target.value})} data-testid="album-nombre" /></div>
            <div><Label>Descripción</Label><Input value={albumForm.descripcion} onChange={e => setAlbumForm({...albumForm, descripcion: e.target.value})} /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAlbumDialog(false)}>Cancelar</Button>
            <Button onClick={handleCreateAlbum} className="bg-teal-600 hover:bg-teal-700 text-white" data-testid="save-album-btn">Crear</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Categoria Dialog */}
      <Dialog open={showCatDialog} onOpenChange={(o) => { setShowCatDialog(o); if (!o) { setEditingCat(null); setCatParentId(null); } }}>
        <DialogContent className="max-w-md dark:bg-slate-800">
          <DialogHeader><DialogTitle>
            {editingCat ? "Renombrar Carpeta" : catParentId ? `Nueva Subcarpeta en "${categorias.find(c => c.id === catParentId)?.nombre || ""}"` : "Nueva Carpeta"}
          </DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div><Label>Nombre *</Label><Input value={catForm.nombre} onChange={e => setCatForm({...catForm, nombre: e.target.value})} data-testid="cat-nombre" /></div>
            {!editingCat && <div><Label>Descripción</Label><Input value={catForm.descripcion} onChange={e => setCatForm({...catForm, descripcion: e.target.value})} /></div>}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCatDialog(false)}>Cancelar</Button>
            <Button onClick={handleCreateCat} className="bg-teal-600 hover:bg-teal-700 text-white" data-testid="save-cat-btn">{editingCat ? "Guardar" : "Crear"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Documento Dialog — subida múltiple con drag & drop */}
      <Dialog open={showDocDialog} onOpenChange={(o) => { if (!uploadingDoc) { setShowDocDialog(o); if (!o) setDocFiles([]); } }}>
        <DialogContent className="max-w-lg dark:bg-slate-800 max-h-[88vh] overflow-y-auto">
          <DialogHeader><DialogTitle>Subir Documentos</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Carpeta destino *</Label>
              <Select value={docForm.categoria_id} onValueChange={v => setDocForm({...docForm, categoria_id: v})}>
                <SelectTrigger><SelectValue placeholder="Seleccionar carpeta" /></SelectTrigger>
                <SelectContent>
                  {categorias.filter(c => !c.parent_id).map(c => (
                    [<SelectItem key={c.id} value={c.id}>{c.nombre}</SelectItem>,
                     ...categorias.filter(s => s.parent_id === c.id).map(s => (
                       <SelectItem key={s.id} value={s.id}>&nbsp;&nbsp;&nbsp;↳ {s.nombre}</SelectItem>
                     ))]
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div><Label>Descripción (opcional, aplica a todos)</Label><Input value={docForm.descripcion} onChange={e => setDocForm({...docForm, descripcion: e.target.value})} /></div>
            <div
              data-testid="doc-dropzone"
              onDragOver={(e) => e.preventDefault()}
              onDrop={(e) => { e.preventDefault(); agregarDocFiles(e.dataTransfer.files); }}
              onClick={() => document.getElementById("doc-multi-input")?.click()}
              className="border-2 border-dashed border-teal-300 dark:border-teal-700 rounded-xl p-6 text-center cursor-pointer hover:bg-teal-50 dark:hover:bg-teal-900/20 transition-colors"
            >
              <UploadCloud className="w-8 h-8 mx-auto text-teal-500 mb-2" />
              <p className="text-sm font-medium text-slate-700 dark:text-slate-300">Arrastra y suelta los archivos aquí</p>
              <p className="text-xs text-slate-500">o haz clic para seleccionarlos (máximo {MAX_DOC_FILES} a la vez)</p>
              <input id="doc-multi-input" type="file" multiple className="hidden" data-testid="doc-file"
                onChange={e => { agregarDocFiles(e.target.files); e.target.value = ""; }} />
            </div>
            {docFiles.length > 0 && (
              <div className="space-y-2 max-h-56 overflow-y-auto" data-testid="doc-files-lista">
                <p className="text-xs text-slate-500">{docFiles.length} archivo{docFiles.length === 1 ? "" : "s"} — puedes editar el nombre de cada uno (si lo dejas, se usa el nombre del archivo)</p>
                {docFiles.map((f, i) => (
                  <div key={i} className="flex items-center gap-2">
                    <FileText className="w-4 h-4 text-slate-400 flex-shrink-0" />
                    <Input value={f.titulo} className="h-8 text-sm"
                      onChange={e => setDocFiles(prev => prev.map((x, j) => j === i ? { ...x, titulo: e.target.value } : x))} />
                    <span className="text-[10px] text-slate-400 flex-shrink-0 w-14 truncate">{(f.file.size / (1024 * 1024)).toFixed(1)} MB</span>
                    <button className="text-red-400 hover:text-red-600 flex-shrink-0" onClick={() => setDocFiles(prev => prev.filter((_, j) => j !== i))}>
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDocDialog(false)} disabled={uploadingDoc}>Cancelar</Button>
            <Button onClick={handleUploadDoc} disabled={uploadingDoc || docFiles.length === 0} className="bg-teal-600 hover:bg-teal-700 text-white" data-testid="save-doc-btn">
              {uploadingDoc && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              {uploadingDoc ? `Subiendo ${uploadDocProgress}...` : `Subir ${docFiles.length || ""} archivo${docFiles.length === 1 ? "" : "s"}`}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Libro Dialog */}
      <Dialog open={showLibroDialog} onOpenChange={setShowLibroDialog}>
        <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto dark:bg-slate-800">
          <DialogHeader><DialogTitle>{editingLibro ? "Editar Libro" : "Agregar Libro"}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div><Label>Título *</Label><Input value={libroForm.titulo} onChange={e => setLibroForm({...libroForm, titulo: e.target.value})} data-testid="libro-titulo" /></div>
              <div><Label>Autor *</Label><Input value={libroForm.autor} onChange={e => setLibroForm({...libroForm, autor: e.target.value})} data-testid="libro-autor" /></div>
            </div>
            <div><Label>Descripción</Label><Textarea rows={2} value={libroForm.descripcion} onChange={e => setLibroForm({...libroForm, descripcion: e.target.value})} /></div>
            <div className="grid grid-cols-2 gap-4">
              <div><Label>Enlace Externo</Label><Input value={libroForm.enlace_externo} onChange={e => setLibroForm({...libroForm, enlace_externo: e.target.value})} placeholder="https://..." /></div>
              <div><Label>Portada</Label><Input type="file" accept="image/*" onChange={e => setLibroPortadaFile(e.target.files[0])} /></div>
            </div>
            <div>
              <Label>Documento PDF <Badge variant="outline" className="ml-2 text-xs">Opcional</Badge></Label>
              {libroForm.pdf_url && (
                <div className="flex items-center gap-2 mb-2 p-2 bg-green-50 dark:bg-green-900/20 rounded-lg border border-green-200 dark:border-green-800">
                  <FileText className="w-4 h-4 text-green-600" />
                  <span className="text-sm text-green-700 dark:text-green-300 flex-1">PDF cargado</span>
                  <a href={resolveFileUrl(libroForm.pdf_url)} target="_blank" rel="noreferrer"><Button variant="outline" size="sm"><Eye className="w-3 h-3 mr-1" /> Ver</Button></a>
                  <Button variant="ghost" size="sm" className="text-red-500" onClick={() => setLibroForm({...libroForm, pdf_url: ""})}><X className="w-3 h-3" /></Button>
                </div>
              )}
              <label className="cursor-pointer">
                <input type="file" accept="application/pdf" className="hidden" onChange={e => e.target.files[0] && handleUploadPdf(e.target.files[0])} />
                <Button asChild variant="outline" disabled={uploadingPdf}>
                  <span>{uploadingPdf ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Upload className="w-4 h-4 mr-2" />} {libroForm.pdf_url ? "Cambiar PDF" : "Subir PDF"}</span>
                </Button>
              </label>
            </div>
            <div>
              <div className="flex items-center justify-between mb-2">
                <Label>Contenido HTML <Badge variant="outline" className="ml-2 text-xs">Opcional</Badge></Label>
                {libroForm.contenido_html && (
                  <Button variant="outline" size="sm" onClick={() => setLibroHtmlPreview(!libroHtmlPreview)}>
                    {libroHtmlPreview ? "Editar" : "Vista Previa"}
                  </Button>
                )}
              </div>
              {libroHtmlPreview ? (
                <div className="border rounded-lg p-4 bg-white dark:bg-slate-900 min-h-[200px] max-h-[400px] overflow-auto">
                  <iframe
                    srcDoc={libroForm.contenido_html}
                    className="w-full min-h-[200px] border-0"
                    style={{ height: "350px" }}
                    title="Preview"
                    sandbox="allow-scripts allow-same-origin"
                  />
                </div>
              ) : (
                <textarea
                  value={libroForm.contenido_html}
                  onChange={e => setLibroForm({...libroForm, contenido_html: e.target.value})}
                  className="w-full min-h-[200px] p-3 font-mono text-sm border rounded-lg bg-slate-950 text-green-400 dark:bg-slate-950 focus:outline-none focus:ring-2 focus:ring-teal-500"
                  placeholder="<!-- Pegue su código HTML aquí -->\n<div style='text-align:center;'>\n  <h1>Mi Libro</h1>\n</div>"
                  data-testid="libro-html-editor"
                />
              )}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowLibroDialog(false)}>Cancelar</Button>
            <Button onClick={handleSaveLibro} disabled={loading} className="bg-teal-600 hover:bg-teal-700 text-white" data-testid="save-libro-btn">
              {loading && <Loader2 className="w-4 h-4 mr-2 animate-spin" />} {editingLibro ? "Actualizar" : "Agregar"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Directorio Dialog */}
      <Dialog open={showDirDialog} onOpenChange={setShowDirDialog}>
        <DialogContent className="max-w-lg dark:bg-slate-800">
          <DialogHeader><DialogTitle>{editingDir ? "Editar Registro" : "Nuevo Registro"}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div><Label>Nombre *</Label><Input value={dirForm.nombre} onChange={e => setDirForm({...dirForm, nombre: e.target.value})} data-testid="dir-nombre" /></div>
              <div>
                <Label>Departamento</Label>
                <Select value={dirForm.departamento} onValueChange={v => setDirForm({...dirForm, departamento: v})}>
                  <SelectTrigger data-testid="dir-departamento">
                    <SelectValue placeholder="Seleccione departamento" />
                  </SelectTrigger>
                  <SelectContent>
                    {directorioDepts.map(d => <SelectItem key={d} value={d}>{d}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div><Label>Extension</Label><Input value={dirForm.extension} onChange={e => setDirForm({...dirForm, extension: e.target.value})} data-testid="dir-extension" placeholder="Ej: 2221" /></div>
              <div><Label>Número Directo</Label><Input value={dirForm.numero_directo} onChange={e => setDirForm({...dirForm, numero_directo: e.target.value})} data-testid="dir-numero-directo" placeholder="Ej: (809)701-6906" /></div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div><Label>Flota</Label><Input value={dirForm.telefono} onChange={e => setDirForm({...dirForm, telefono: e.target.value})} data-testid="dir-telefono" placeholder="Ej: 809-470-1199" /></div>
              <div><Label>Correo electrónico</Label><Input type="email" value={dirForm.correo} onChange={e => setDirForm({...dirForm, correo: e.target.value})} data-testid="dir-correo" placeholder="correo@qasesores.com.do" /></div>
            </div>
            <div><Label>Cargo</Label><Input value={dirForm.cargo} onChange={e => setDirForm({...dirForm, cargo: e.target.value})} data-testid="dir-cargo" placeholder="Ej: Gerente IT" /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDirDialog(false)}>Cancelar</Button>
            <Button onClick={handleSaveDir} disabled={loading} className="bg-teal-600 hover:bg-teal-700 text-white" data-testid="save-dir-btn">
              {loading && <Loader2 className="w-4 h-4 mr-2 animate-spin" />} Guardar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Galería HTML Editor Dialog */}
      <Dialog open={showGaleriaHtmlDialog} onOpenChange={setShowGaleriaHtmlDialog}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto dark:bg-slate-800">
          <DialogHeader><DialogTitle>{editingGaleriaHtml ? "Editar Publicación HTML" : "Nueva Publicación HTML en Galería"}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div><Label>Título *</Label><Input value={galeriaHtmlForm.titulo} onChange={e => setGaleriaHtmlForm({...galeriaHtmlForm, titulo: e.target.value})} data-testid="galeria-html-titulo" /></div>
              <div><Label>Descripción</Label><Input value={galeriaHtmlForm.descripcion} onChange={e => setGaleriaHtmlForm({...galeriaHtmlForm, descripcion: e.target.value})} /></div>
            </div>
            <div>
              <div className="flex items-center justify-between mb-2">
                <Label>Código HTML *</Label>
                <Button variant="outline" size="sm" onClick={() => setGaleriaHtmlPreview(!galeriaHtmlPreview)}>
                  {galeriaHtmlPreview ? "Editar Código" : "Vista Previa"}
                </Button>
              </div>
              {galeriaHtmlPreview ? (
                <div className="border rounded-lg bg-white dark:bg-slate-900 overflow-hidden">
                  <iframe srcDoc={galeriaHtmlForm.contenido_html} className="w-full border-0" style={{ height: "450px" }} title="Preview" sandbox="allow-scripts allow-same-origin" />
                </div>
              ) : (
                <textarea
                  value={galeriaHtmlForm.contenido_html}
                  onChange={e => setGaleriaHtmlForm({...galeriaHtmlForm, contenido_html: e.target.value})}
                  className="w-full p-3 font-mono text-sm border rounded-lg bg-slate-950 text-green-400 focus:outline-none focus:ring-2 focus:ring-teal-500"
                  style={{ minHeight: "350px" }}
                  placeholder={"<!-- Código HTML -->\n<div style='text-align:center; padding: 20px;'>\n  <h1>Mi Contenido</h1>\n</div>"}
                  data-testid="galeria-html-editor"
                />
              )}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowGaleriaHtmlDialog(false)}>Cancelar</Button>
            <Button onClick={handleSaveGaleriaHtml} disabled={loading} className="bg-teal-600 hover:bg-teal-700 text-white" data-testid="save-galeria-html-btn">
              {loading && <Loader2 className="w-4 h-4 mr-2 animate-spin" />} {editingGaleriaHtml ? "Actualizar" : "Crear"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Galería HTML Viewer (fullscreen) */}
      {viewingGaleriaHtml && (
        <div className="fixed inset-0 z-50 bg-white dark:bg-slate-900 flex flex-col">
          <div className="flex items-center justify-between px-6 py-3 border-b bg-slate-50 dark:bg-slate-800 flex-shrink-0">
            <div>
              <h2 className="font-bold text-lg text-slate-900 dark:text-white">{viewingGaleriaHtml.titulo}</h2>
              {viewingGaleriaHtml.descripcion && <p className="text-sm text-slate-500">{viewingGaleriaHtml.descripcion}</p>}
            </div>
            <Button variant="outline" onClick={() => setViewingGaleriaHtml(null)}><X className="w-4 h-4 mr-2" /> Cerrar</Button>
          </div>
          <iframe srcDoc={viewingGaleriaHtml.contenido_html} className="flex-1 w-full border-0" title={viewingGaleriaHtml.titulo} sandbox="allow-scripts allow-same-origin" />
        </div>
      )}

      {/* HTML Document Editor Dialog */}
      <Dialog open={showHtmlDocDialog} onOpenChange={setShowHtmlDocDialog}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto dark:bg-slate-800">
          <DialogHeader><DialogTitle>{editingHtmlDoc ? "Editar Publicación HTML" : "Nueva Publicación HTML"}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div><Label>Título *</Label><Input value={htmlDocForm.titulo} onChange={e => setHtmlDocForm({...htmlDocForm, titulo: e.target.value})} data-testid="html-doc-titulo" /></div>
              <div>
                <Label>Categoría *</Label>
                <Select value={htmlDocForm.categoria_id} onValueChange={v => setHtmlDocForm({...htmlDocForm, categoria_id: v})}>
                  <SelectTrigger><SelectValue placeholder="Seleccionar" /></SelectTrigger>
                  <SelectContent>{categorias.map(c => <SelectItem key={c.id} value={c.id}>{c.nombre}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
            <div><Label>Descripción</Label><Input value={htmlDocForm.descripcion} onChange={e => setHtmlDocForm({...htmlDocForm, descripcion: e.target.value})} /></div>
            <div>
              <div className="flex items-center justify-between mb-2">
                <Label>Código HTML *</Label>
                <Button variant="outline" size="sm" onClick={() => setHtmlPreviewMode(!htmlPreviewMode)}>
                  {htmlPreviewMode ? "Editar Código" : "Vista Previa"}
                </Button>
              </div>
              {htmlPreviewMode ? (
                <div className="border rounded-lg bg-white dark:bg-slate-900 overflow-hidden">
                  <iframe
                    srcDoc={htmlDocForm.contenido_html}
                    className="w-full border-0"
                    style={{ height: "450px" }}
                    title="Preview"
                    sandbox="allow-scripts allow-same-origin"
                  />
                </div>
              ) : (
                <textarea
                  value={htmlDocForm.contenido_html}
                  onChange={e => setHtmlDocForm({...htmlDocForm, contenido_html: e.target.value})}
                  className="w-full p-3 font-mono text-sm border rounded-lg bg-slate-950 text-green-400 focus:outline-none focus:ring-2 focus:ring-teal-500"
                  style={{ minHeight: "350px" }}
                  placeholder={"<!-- Código HTML completo -->\n<!DOCTYPE html>\n<html>\n<head>\n  <style>\n    body { font-family: sans-serif; padding: 20px; }\n    .title { color: #0d9488; font-size: 24px; }\n  </style>\n</head>\n<body>\n  <h1 class=\"title\">Mi Publicación</h1>\n  <p>Contenido aquí...</p>\n</body>\n</html>"}
                  data-testid="html-doc-editor"
                />
              )}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowHtmlDocDialog(false)}>Cancelar</Button>
            <Button onClick={handleSaveHtmlDoc} disabled={loading} className="bg-teal-600 hover:bg-teal-700 text-white" data-testid="save-html-doc-btn">
              {loading && <Loader2 className="w-4 h-4 mr-2 animate-spin" />} {editingHtmlDoc ? "Actualizar" : "Crear"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* HTML Document Viewer (fullscreen) */}
      {viewingHtmlDoc && (
        <div className="fixed inset-0 z-50 bg-white dark:bg-slate-900 flex flex-col">
          <div className="flex items-center justify-between px-6 py-3 border-b bg-slate-50 dark:bg-slate-800 flex-shrink-0">
            <div>
              <h2 className="font-bold text-lg text-slate-900 dark:text-white">{viewingHtmlDoc.titulo}</h2>
              {viewingHtmlDoc.descripcion && <p className="text-sm text-slate-500">{viewingHtmlDoc.descripcion}</p>}
            </div>
            <Button variant="outline" onClick={() => setViewingHtmlDoc(null)} data-testid="close-html-viewer"><X className="w-4 h-4 mr-2" /> Cerrar</Button>
          </div>
          <iframe
            srcDoc={viewingHtmlDoc.contenido_html}
            className="flex-1 w-full border-0"
            title={viewingHtmlDoc.titulo}
            sandbox="allow-scripts allow-same-origin"
          />
        </div>
      )}

      {/* Libro HTML Viewer (fullscreen) */}
      {viewingLibro && (
        <div className="fixed inset-0 z-50 bg-white dark:bg-slate-900 flex flex-col">
          <div className="flex items-center justify-between px-6 py-3 border-b bg-slate-50 dark:bg-slate-800 flex-shrink-0">
            <div>
              <h2 className="font-bold text-lg text-slate-900 dark:text-white">{viewingLibro.titulo}</h2>
            </div>
            <Button variant="outline" onClick={() => setViewingLibro(null)} data-testid="close-libro-viewer"><X className="w-4 h-4 mr-2" /> Cerrar</Button>
          </div>
          <iframe
            srcDoc={viewingLibro.contenido_html}
            className="flex-1 w-full border-0"
            title={viewingLibro.titulo}
            sandbox="allow-scripts allow-same-origin"
          />
        </div>
      )}

      {/* Carousel Create Dialog */}
      <Dialog open={showCarouselDialog} onOpenChange={setShowCarouselDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Nuevo Carrusel de Imágenes</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Título *</Label>
              <Input
                value={carouselForm.titulo}
                onChange={e => setCarouselForm({ ...carouselForm, titulo: e.target.value })}
                placeholder="Ej: Evento de fin de año"
                data-testid="carousel-titulo-input"
              />
            </div>
            <div>
              <Label>Descripción</Label>
              <Textarea
                value={carouselForm.descripcion}
                onChange={e => setCarouselForm({ ...carouselForm, descripcion: e.target.value })}
                placeholder="Descripción opcional"
                rows={2}
              />
            </div>
            <div>
              <Label>Efecto de Transición</Label>
              <select className="w-full border rounded-lg px-3 py-2 mt-1 dark:bg-slate-700 dark:border-slate-600 text-sm"
                value={carouselForm.efecto_transicion || "fade"}
                onChange={e => setCarouselForm({ ...carouselForm, efecto_transicion: e.target.value })}
                data-testid="carousel-efecto-select">
                {EFECTOS.map(e => <option key={e.value} value={e.value}>{e.label}</option>)}
              </select>
            </div>
            <div>
              <Label>Imágenes * ({carouselFiles.length} seleccionada(s))</Label>
              <input
                type="file"
                multiple
                accept="image/*"
                onChange={e => setCarouselFiles(Array.from(e.target.files || []))}
                className="mt-1 block w-full text-sm file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:bg-teal-50 file:text-teal-700 hover:file:bg-teal-100"
                data-testid="carousel-files-input"
              />
              {carouselFiles.length > 0 && (
                <div className="flex gap-2 mt-2 flex-wrap">
                  {carouselFiles.map((f, i) => (
                    <div key={i} className="w-16 h-16 rounded border overflow-hidden">
                      <img src={URL.createObjectURL(f)} alt="" className="w-full h-full object-cover" />
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCarouselDialog(false)}>Cancelar</Button>
            <Button onClick={handleCreateCarousel} disabled={uploadingCarousel} className="bg-teal-600 hover:bg-teal-700 text-white" data-testid="save-carousel-btn">
              {uploadingCarousel && <Loader2 className="w-4 h-4 mr-2 animate-spin" />} Crear Carrusel
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* PDF Document Viewer (fullscreen) */}
      {viewingPdfDoc && (
        pdfBlobUrl ? (
          <PdfViewer
            url={pdfBlobUrl}
            title={viewingPdfDoc.titulo}
            description={viewingPdfDoc.descripcion}
            onClose={closePdfViewer}
            onDownload={() => {
              const a = document.createElement("a");
              a.href = resolveFileUrl(viewingPdfDoc.url);
              a.download = viewingPdfDoc.nombre_archivo || viewingPdfDoc.titulo || "documento.pdf";
              a.click();
            }}
          />
        ) : pdfLoadError ? (
          <div className="fixed inset-0 z-50 bg-white dark:bg-slate-900 flex flex-col items-center justify-center" data-testid="pdf-viewer-modal">
            <FileText className="w-16 h-16 text-slate-300 mb-4" />
            <p className="text-lg font-medium text-slate-700 dark:text-slate-300">No se pudo cargar el documento</p>
            <p className="text-sm text-slate-500 mt-2">El archivo PDF no se encuentra disponible.</p>
            <div className="flex gap-3 mt-6">
              <a href={resolveFileUrl(viewingPdfDoc.url)} download>
                <Button variant="outline"><Download className="w-4 h-4 mr-2" /> Descargar</Button>
              </a>
              <Button variant="ghost" onClick={closePdfViewer}>Volver</Button>
            </div>
          </div>
        ) : (
          <div className="fixed inset-0 z-50 bg-white dark:bg-slate-900 flex items-center justify-center" data-testid="pdf-viewer-modal">
            <Loader2 className="w-8 h-8 animate-spin text-teal-600" />
            <span className="ml-3 text-slate-500">Cargando documento...</span>
          </div>
        )
      )}

      {/* Lightbox */}
      {lightboxFoto && (
        <div className="fixed inset-0 z-50 bg-black/90 flex items-center justify-center" onClick={() => setLightboxFoto(null)}>
          <button className="absolute top-4 right-4 text-white" onClick={() => setLightboxFoto(null)}><X className="w-8 h-8" /></button>
          <img src={resolveFileUrl(lightboxFoto.url)} alt="" className="max-w-[90vw] max-h-[90vh] object-contain" onClick={e => e.stopPropagation()} />
        </div>
      )}
    </div>
      </main>
    </div>
  );
}
